import React, { createContext, useState, useEffect, useCallback, useContext, useRef } from "react";
import { ChevronLeft, ChevronRight, ChevronDown, ChevronUp, Copy, Trash2 } from "lucide-react";
import CalendarCopyDialog from "./CalendarCopyDialog";
import { maxMinutesInShift, minShiftMinutes, placeInShift, shiftWindow } from "./shiftPlacement";
import { buildDayLanes } from "./dayLanes";
import { actualsToEvents } from "./calendarActuals";

const WEEKDAYS_SHORT = ["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"];
const WEEKDAYS_LONG  = ["Monday","Tuesday","Wednesday","Thursday","Friday","Saturday","Sunday"];
const MONTHS = ["January","February","March","April","May","June","July",
                "August","September","October","November","December"];
// Minden cleaning group saját színt kap, hogy a naptárban megkülönböztethetők legyenek.
const CLEANING_GROUP_PALETTE = ["#0891b2", "#7c3aed", "#db2777", "#2563eb", "#ea580c", "#65a30d", "#9333ea", "#0369a1"];
// Másolás megnyitása a chipekből és a napfejlécekből: openCopy("item", event) / openCopy("day", date).
const CalendarCopyContext = createContext(null);

function DayCopyButton({ day }) {
  const copy = useContext(CalendarCopyContext);
  if (!copy) return null;
  return (
    <button
      type="button"
      title="Copy day"
      aria-label={`Copy day ${localDateStr(day)}`}
      onClick={(event) => { event.stopPropagation(); copy.openCopy("day", day); }}
      className="text-slate-300 hover:text-teal-600"
    >
      <Copy size={11} />
    </button>
  );
}

function cleaningGroupColor(groupId) {
  return CLEANING_GROUP_PALETTE[Math.abs(Number(groupId) || 0) % CLEANING_GROUP_PALETTE.length];
}
// Shift és dolgozó: az oldal slate színe (a korábbi sárga helyett).
const SHIFT_COLOR = "#475569";
const SHIFT_BAND_W = 26;
const ROUTE_PALETTE = ['#0F766E','#10B981','#F59E0B','#0e9488','#059669','#d97706','#134e4a','#065f46'];

// px height per hour in time-grid views
const SLOT_H = 56;

// Convert hex color to rgba with given alpha (0-1)
function hexToRgba(hex, alpha = 0.72) {
  const h = hex.replace("#", "");
  const r = parseInt(h.slice(0, 2), 16);
  const g = parseInt(h.slice(2, 4), 16);
  const b = parseInt(h.slice(4, 6), 16);
  return `rgba(${r},${g},${b},${alpha})`;
}

// ── Helpers ──────────────────────────────────────────────────────────────────
function isSameDay(a, b) {
  return a.getFullYear() === b.getFullYear()
    && a.getMonth() === b.getMonth()
    && a.getDate() === b.getDate();
}
function startOfWeek(date) {
  const d = new Date(date);
  const day = d.getDay();
  d.setDate(d.getDate() + (day === 0 ? -6 : 1 - day));
  d.setHours(0, 0, 0, 0);
  return d;
}
function addDays(date, n) {
  const d = new Date(date); d.setDate(d.getDate() + n); return d;
}
function localDateStr(date) {
  const y = date.getFullYear();
  const m = String(date.getMonth() + 1).padStart(2, "0");
  const d = String(date.getDate()).padStart(2, "0");
  return `${y}-${m}-${d}`;
}

// Sort chips: combined (route+cleaner) first, then route-only, then cleaner-only
function sortChips(evs) {
  return [...evs].sort((a, b) => {
    const rank = e => (e.hasRoute && e.assigned_user_id) ? 0 : e.hasRoute ? 1 : 2;
    return rank(a) - rank(b);
  });
}

// ── Avatar ───────────────────────────────────────────────────────────────────
function Avatar({ name, photo, size = "sm" }) {
  const initial = (name || "?").trim()[0]?.toUpperCase() || "?";
  const cls = size === "lg"
    ? "w-8 h-8 text-[0.75rem]"
    : size === "md"
    ? "w-6 h-6 text-[11px]"
    : "w-5 h-5 text-[11px]";
  return (
    <div className={`${cls} rounded-full border-2 border-white/60 bg-white flex items-center justify-center flex-shrink-0 overflow-hidden`}
         title={name}>
      {photo
        ? <img src={photo.startsWith('http') || photo.startsWith('/') ? photo : '/' + photo} alt="" className="w-full h-full object-cover" draggable={false} />
        : <span className="font-bold text-slate-700 leading-none">{initial}</span>}
    </div>
  );
}

// ── Event chip – rounded card, label strip at top, cleaner badge inside ───────
export function EventChip({ event, onAssignCleaner, onAssignRoute, onAssignShift, onUnassignCleaner, onDelete, onResizeDone, onDropThrough, style, className, minMinutes = null, maxMinutes = null, inLane = false }) {
  const [over, setOver] = React.useState(false);
  const [hovered, setHovered] = React.useState(false);
  const [resizingHeight, setResizingHeight] = React.useState(null);
  const chipRef = useRef(null);
  const resizeStateRef = useRef(null);
  const hasUser = !!event.assigned_user_name;
  const copy = useContext(CalendarCopyContext);
  const rawColor  = event.color || "#0F766E";
  // Shifts and legacy cleaner-only chips use amber; routes keep their own color.
  const routeColor = event.is_shift || (!event.hasRoute && hasUser) ? SHIFT_COLOR : rawColor;
  const chipBg    = hexToRgba(routeColor, 0.15);
  const chipBorder = hexToRgba(routeColor, 0.70);
  const stripBg   = hexToRgba(routeColor, 0.75);
  // Inner cleaner card: same style as outer chip but amber
  const cleanerCardBg     = hexToRgba(SHIFT_COLOR, 0.10);
  const cleanerCardBorder = hexToRgba(SHIFT_COLOR, 0.45);
  const tooltipText = event.is_shift
    ? `${event.title}${hasUser ? ` – ${event.assigned_user_name}` : " – Unassigned"}`
    : event.hasRoute && hasUser
    ? `${event.title} – ${event.assigned_user_name}`
    : event.hasRoute ? event.title
    : event.assigned_user_name || event.title;

  const handleResizePointerDown = (e) => {
    e.preventDefault();
    e.stopPropagation();
    const initialHeight = chipRef.current?.getBoundingClientRect().height || SLOT_H;
    const initialMinutes = event.estimated_minutes || 60;
    resizeStateRef.current = { startY: e.clientY, initialHeight, initialMinutes };
    // Shiften belüli group nem nyúlhat túl a shift végén; a shift nem lehet rövidebb a groupjainál.
    const clampHeight = (h) => {
      let height = Math.max(SLOT_H * 0.3, h);
      if (maxMinutes != null) height = Math.min(height, (maxMinutes / 60) * SLOT_H);
      if (minMinutes != null) height = Math.max(height, (minMinutes / 60) * SLOT_H);
      return height;
    };
    const handleMove = (ev) => {
      const delta = ev.clientY - resizeStateRef.current.startY;
      setResizingHeight(clampHeight(resizeStateRef.current.initialHeight + delta));
    };
    const handleUp = (ev) => {
      document.removeEventListener('pointermove', handleMove);
      document.removeEventListener('pointerup', handleUp);
      if (!resizeStateRef.current) return;
      const delta = ev.clientY - resizeStateRef.current.startY;
      const newH = clampHeight(resizeStateRef.current.initialHeight + delta);
      let newMinutes = Math.max(15, Math.round((newH / SLOT_H) * 60));
      if (maxMinutes != null) newMinutes = Math.min(newMinutes, maxMinutes);
      if (minMinutes != null) newMinutes = Math.max(newMinutes, minMinutes);
      resizeStateRef.current = null;
      setResizingHeight(null);
      if (onResizeDone) onResizeDone(event.id, newMinutes);
    };
    document.addEventListener('pointermove', handleMove);
    document.addEventListener('pointerup', handleUp);
  };

  const moveDragData = JSON.stringify({
    __moveEventId: event.id, title: event.title, color: event.color, hasTime: event.hasTime,
  });
  const cleanerDragData = JSON.stringify({
    __unassignFrom: event.id,
    __sourceIsShift: !!event.is_shift,
    type: "cleaner",
    id: event.assigned_user_id,
    label: event.assigned_user_name,
    shift_minutes: event.assigned_user_shift_minutes || 480,
    root_location_id: event.root_location_id || null,
  });
  // Napi nézet oszlopában a dolgozó kis képe a shift fejlécében, a terv-oszlop felett.
  const shiftAvatarPos = { right: 5 };
  const shiftAssignmentDragData = event.hasRoute && !event.is_shift
    ? JSON.stringify({ __sourcePlanId: event.id, label: event.title })
    : null;

  return (
    <div
      ref={chipRef}
      draggable
      title={tooltipText}
      style={{
        position: 'relative',
        backgroundColor: chipBg,
        border: `1.5px solid ${chipBorder}`,
        borderRadius: 6,
        ...style,
        ...(resizingHeight !== null ? { height: resizingHeight } : {}),
      }}
      className={`flex flex-col select-none cursor-grab ${over ? "ring-2 ring-teal-300" : ""} ${className || ""}`}
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
      onDragStart={e => {
        e.dataTransfer.setDragImage(e.currentTarget, 0, 0);
        e.dataTransfer.setData("application/iclean-move", moveDragData);
        if (event.cleaning_group_id && !event.is_shift) {
          activeGroupDrag = { minutes: event.estimated_minutes || 60, sourceEventId: event.id, groupId: event.cleaning_group_id, label: event.title };
        }
        if (shiftAssignmentDragData) {
          e.dataTransfer.setData("application/iclean-shift-assignment", shiftAssignmentDragData);
        }
        // Chip-to-chip merge: ha tiszta route vagy cleaner chip, a cél chip is tudjon reagálni
        if (event.hasRoute && !hasUser && event.cleaning_group_id) {
          e.dataTransfer.setData("application/iclean-plan", JSON.stringify({
            type: "cleaning_group", id: event.cleaning_group_id, label: event.title,
            duration_minutes: event.estimated_minutes,
            __sourcePlanId: event.id,
          }));
          e.dataTransfer.setData("application/iclean-plantype-route", "1");
        } else if (event.hasRoute && !hasUser && event.route_id) {
          e.dataTransfer.setData("application/iclean-plan", JSON.stringify({
            type: "route", id: event.route_id, label: event.title,
            duration_minutes: event.estimated_minutes,
            __sourcePlanId: event.id,
          }));
          e.dataTransfer.setData("application/iclean-plantype-route", "1");
        } else if (!event.hasRoute && !event.is_shift && hasUser) {
          e.dataTransfer.setData("application/iclean-plan", JSON.stringify({
            type: "cleaner", id: event.assigned_user_id,
            label: event.assigned_user_name,
            shift_minutes: event.assigned_user_shift_minutes || 480,
            __sourcePlanId: event.id,
          }));
          e.dataTransfer.setData("application/iclean-plantype-cleaner", "1");
        }
        e.dataTransfer.effectAllowed = "move";
        const el = e.currentTarget;
        setTimeout(() => { if (el) el.style.pointerEvents = 'none'; }, 0);
      }}
      onDragEnd={e => { activeGroupDrag = null; if (chipRef.current) chipRef.current.style.pointerEvents = ''; }}
      onDragOver={e => {
        if (event.is_shift && e.dataTransfer.types.includes("application/iclean-shift-assignment")) {
          e.preventDefault(); e.stopPropagation(); setOver(true);
          return;
        }
        if (!e.dataTransfer.types.includes("application/iclean-plan")) return;
        const draggingCleaner = e.dataTransfer.types.includes("application/iclean-plantype-cleaner");
        const draggingRoute   = e.dataTransfer.types.includes("application/iclean-plantype-route");
        const canMerge = (draggingCleaner && (event.hasRoute || event.is_shift) && !hasUser)
                       || (draggingRoute && !event.is_shift && !event.hasRoute && hasUser);
        const sameType = (draggingCleaner && !event.hasRoute && !event.is_shift)
                       || (draggingRoute   && event.hasRoute);
        if (canMerge) { e.preventDefault(); e.stopPropagation(); setOver(true); }
        // Same-type: accept drop on chip but forward to parent via onDropThrough
        else if (sameType && onDropThrough) { e.preventDefault(); e.stopPropagation(); }
      }}
      onDragLeave={() => setOver(false)}
      onDrop={e => {
        const shiftRaw = e.dataTransfer.getData("application/iclean-shift-assignment");
        if (shiftRaw && event.is_shift && onAssignShift) {
          e.preventDefault(); e.stopPropagation(); setOver(false);
          try { onAssignShift(event.id, JSON.parse(shiftRaw)); } catch {}
          return;
        }
        const raw = e.dataTransfer.getData("application/iclean-plan");
        if (!raw) return;
        e.preventDefault(); e.stopPropagation(); setOver(false);
        try {
          const data = JSON.parse(raw);
          if (data.type === "cleaner" && (event.hasRoute || event.is_shift) && !hasUser && onAssignCleaner) {
            onAssignCleaner(event.id, data);
          } else if ((data.type === "route" || data.type === "cleaning_group") && !event.is_shift && !event.hasRoute && hasUser && onAssignRoute) {
            onAssignRoute(event.id, data);
          } else if (onDropThrough) {
            // Same-type: forward to parent (TimedEventsColumn) to create new plan at same time
            onDropThrough(e);
          }
        } catch {}
      }}
    >
      {/* Napi nézet oszlopában a shift bal szélén függőleges sáv: kép, név, idő */}
      {inLane && event.is_shift && (
        <div className="pointer-events-none absolute inset-y-0 left-0 flex flex-col items-center gap-1 overflow-hidden py-1"
          style={{ width: SHIFT_BAND_W, backgroundColor: stripBg, borderRadius: '5px 0 0 5px' }}>
          <div className="flex-shrink-0 overflow-hidden rounded-full border-2 border-white/80 bg-white" style={{ width: SHIFT_BAND_W - 6, height: SHIFT_BAND_W - 6 }}>
            {event.assigned_user_photo
              ? <img src={event.assigned_user_photo.startsWith('http') || event.assigned_user_photo.startsWith('/') ? event.assigned_user_photo : '/' + event.assigned_user_photo} alt="" className="h-full w-full object-cover" draggable={false} />
              : <span className="flex h-full w-full items-center justify-center text-[0.7rem] font-bold text-slate-700">{(event.assigned_user_name || '?')[0].toUpperCase()}</span>}
          </div>
          <span className="whitespace-nowrap text-[0.72rem] font-bold text-white" style={{ writingMode: 'vertical-rl', transform: 'rotate(180deg)' }}>
            {event.assigned_user_name || "Unassigned"}
          </span>
        </div>
      )}
      {/* Top label strip */}
      {!(inLane && event.is_shift) && <div
        className={`flex items-center ${inLane && event.is_shift ? "justify-end pr-7" : "justify-center"} px-1 py-1 flex-shrink-0 pointer-events-none`}
        style={{ backgroundColor: stripBg }}
      >
        {event.is_shift ? (
          <span className="block max-w-full truncate text-white text-[11px] font-extrabold leading-none tracking-tight drop-shadow-sm">
            {event.title || "Shift"}
          </span>
        ) : event.cleaning_group_id ? (
          <span className="block max-w-full truncate text-white text-[11px] font-extrabold leading-none tracking-tight drop-shadow-sm">
            {event.title}
          </span>
        ) : event.hasRoute ? (
          <span className="text-white text-[0.72rem] font-extrabold leading-none tracking-tight drop-shadow-sm">
            {event.route_code || "R"}
          </span>
        ) : (
          <Avatar name={event.assigned_user_name} photo={event.assigned_user_photo} size="md" />
        )}
      </div>}

      {!event.is_shift && event.shift_plan_id && !inLane && (
        <div
          className="truncate border-b border-amber-300/60 bg-amber-50 px-1 py-0.5 text-center text-[11px] font-semibold text-amber-800"
          title={event.shift_assigned_user_name
            ? `${event.shift_label} – ${event.shift_assigned_user_name}`
            : event.shift_label}
        >
          {event.shift_label || "Shift"}
        </div>
      )}

      {/* Combined: inner cleaner card – clearly distinct, draggable to unassign */}
      {hasUser && (event.hasRoute || event.is_shift) && !(inLane && event.is_shift) && (
        <div
          draggable
          onDragStart={e => {
            e.stopPropagation();
            e.dataTransfer.setData("application/iclean-move", cleanerDragData);
            e.dataTransfer.effectAllowed = "move";
          }}
          onDragEnd={e => {
            if (e.dataTransfer.dropEffect === "none" && onUnassignCleaner) {
              onUnassignCleaner(event.id);
            }
          }}
          className={event.is_shift
            ? "group relative flex items-center justify-center cursor-grab flex-shrink-0"
            : "flex items-center justify-center cursor-grab flex-1 overflow-hidden"}
          style={inLane && event.is_shift
            ? { position: 'absolute', top: 1, right: 'auto', left: 'auto', zIndex: 3, ...shiftAvatarPos }
            : {
                margin: '4px',
                backgroundColor: cleanerCardBg,
                border: `1.5px solid ${cleanerCardBorder}`,
                borderRadius: 5,
                minHeight: 28,
                ...(event.is_shift ? { padding: '3px 0' } : {}),
              }}
        >
          {/* Shiftnél a dolgozó piktogramja a shift tetején, a neve egérrámutatásra. */}
          {event.is_shift && <HoverTip>{event.assigned_user_name}</HoverTip>}
          {/* Flexible avatar: fills the inner card width, shows photo or initial */}
          <div style={{
            width: inLane && event.is_shift ? 18 : event.is_shift ? 30 : '80%', maxWidth: 48, aspectRatio: '1',
            borderRadius: '50%',
            border: '2px solid rgba(255,255,255,0.7)',
            backgroundColor: 'white',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            overflow: 'hidden', flexShrink: 0,
          }}>
            {event.assigned_user_photo
              ? <img src={event.assigned_user_photo.startsWith('http') || event.assigned_user_photo.startsWith('/') ? event.assigned_user_photo : '/' + event.assigned_user_photo} alt="" style={{ width: '100%', height: '100%', objectFit: 'cover' }} draggable={false} />
              : <span style={{ fontWeight: 700, color: '#374151', fontSize: '0.75rem', lineHeight: 1 }}>
                  {(event.assigned_user_name || '?')[0].toUpperCase()}
                </span>}
          </div>
        </div>
      )}

      {/* Standalone cleaner – body area (not separately draggable; outer chip handles move) */}
      {!event.hasRoute && !event.is_shift && hasUser && (
        <div className="flex-1" />
      )}

      {event.is_shift && !hasUser && (
        <div className="flex flex-1 items-center justify-center px-1 text-[11px] font-semibold text-amber-900/70">
          Unassigned
        </div>
      )}

      {/* Copy button on hover */}
      {hovered && copy && String(event.id).startsWith("plan-") && (
        <button
          onClick={e => { e.stopPropagation(); copy.openCopy("item", event); }}
          className="absolute top-0 right-4 w-4 h-4 flex items-center justify-center bg-black/40 hover:bg-teal-600 rounded-b z-20"
          title="Copy"
          aria-label="Copy plan"
        >
          <Copy size={8} className="text-white" strokeWidth={2.5} />
        </button>
      )}

      {/* Delete button on hover */}
      {hovered && onDelete && (
        <button
          onClick={e => { e.stopPropagation(); onDelete(event.id); }}
          className="absolute top-0 right-0 w-4 h-4 flex items-center justify-center bg-black/40 hover:bg-red-600 rounded-bl z-20"
          title="Delete"
        >
          <Trash2 size={8} className="text-white" strokeWidth={2.5} />
        </button>
      )}

      {/* Resize handle – bottom edge */}
      {onResizeDone && (
        <div
          onPointerDown={handleResizePointerDown}
          style={{
            position: 'absolute', bottom: 0, left: 0, right: 0, height: 7,
            cursor: 'ns-resize', zIndex: 20,
            background: hovered ? 'rgba(255,255,255,0.3)' : 'transparent',
            borderTop: hovered ? '2px solid rgba(255,255,255,0.5)' : 'none',
          }}
        />
      )}
    </div>
  );
}

// ── Draggable panel rows ──────────────────────────────────────────────────────
// Az éppen húzott cleaning group (listából vagy a naptárból). A dragover alatt a
// dataTransfer tartalma nem olvasható, a shiftbe illesztéshez viszont kell a hossza.
let activeGroupDrag = null;

function makeDragData(type, id, label, extra = {}) {
  return JSON.stringify({ type, id, label, ...extra });
}

function DraggableListRow({ label, color, dragData, extra, itemType }) {
  return (
    <div draggable
      onDragStart={e => {
        e.dataTransfer.setData("application/iclean-plan", dragData);
        if (itemType) e.dataTransfer.setData(`application/iclean-plantype-${itemType}`, "1");
        e.dataTransfer.effectAllowed = "copy";
        try {
          const data = JSON.parse(dragData);
          if (data.type === "cleaning_group") {
            activeGroupDrag = { minutes: data.duration_minutes || 60, groupId: data.id, label: data.label };
          }
        } catch {}
      }}
      onDragEnd={() => { activeGroupDrag = null; }}
      className="flex items-center gap-2 px-2 py-2 rounded border bg-slate-800 border-slate-600 text-slate-200 cursor-grab select-none"
    >
      <div className="w-4 h-4 rounded-sm flex-shrink-0" style={{ backgroundColor: color || "#64748b" }} />
      <span className="text-[0.7rem] font-semibold truncate flex-1">{label}</span>
      {extra}
    </div>
  );
}

function RouteRow({ route, idx, onDurationChange }) {
  const [editing, setEditing] = React.useState(false);
  const [val, setVal] = React.useState(route.duration_minutes ? (route.duration_minutes / 60).toFixed(1) : "");
  const color = ROUTE_PALETTE[idx % ROUTE_PALETTE.length];
  const dragData = makeDragData("route", route.id, `${route.code} – ${route.name}`, {
    duration_minutes: route.duration_minutes || null,
  });

  const save = () => {
    setEditing(false);
    const mins = Math.round(parseFloat(val) * 60);
    if (!isNaN(mins) && mins > 0 && mins !== route.duration_minutes) {
      onDurationChange(route.id, mins);
    }
  };

  return (
    <DraggableListRow
      label={`${route.code} – ${route.name}`}
      color={color}
      dragData={dragData}
      itemType="route"
      extra={
        editing ? (
          <input
            autoFocus
            type="number" min="0.5" max="24" step="0.5"
            value={val}
            onChange={e => setVal(e.target.value)}
            onBlur={save}
            onKeyDown={e => { if (e.key === "Enter") save(); if (e.key === "Escape") setEditing(false); }}
            onClick={e => e.stopPropagation()}
            onMouseDown={e => e.stopPropagation()}
            className="w-12 text-[11px] text-slate-900 bg-white rounded px-1 py-0.5 outline-none"
          />
        ) : (
          <button
            onClick={e => { e.stopPropagation(); setEditing(true); }}
            onMouseDown={e => e.stopPropagation()}
            className="text-[11px] text-slate-400 hover:text-white flex-shrink-0 tabular-nums"
            title="Edit route duration"
          >
            {route.duration_minutes ? `${(route.duration_minutes / 60).toFixed(1)}h` : "—h"}
          </button>
        )
      }
    />
  );
}

// Cleaning group sor: húzható, és az időtartama (alapértelmezett tervhossz) itt állítható.
function GroupRow({ group, onDurationChange }) {
  const [editing, setEditing] = React.useState(false);
  const duration = group.duration_minutes || 60;
  const [val, setVal] = React.useState((duration / 60).toFixed(1));
  const dragData = makeDragData("cleaning_group", group.id, group.name, { duration_minutes: duration });

  const save = () => {
    setEditing(false);
    const mins = Math.round(parseFloat(val) * 60);
    if (!isNaN(mins) && mins > 0 && mins !== duration) onDurationChange(group.id, mins);
  };

  return (
    <DraggableListRow
      label={group.name}
      color={cleaningGroupColor(group.id)}
      dragData={dragData}
      itemType="route"
      extra={
        editing ? (
          <input
            autoFocus
            type="number" min="0.25" max="24" step="0.25"
            aria-label={`Duration of ${group.name} in hours`}
            value={val}
            onChange={e => setVal(e.target.value)}
            onBlur={save}
            onKeyDown={e => { if (e.key === "Enter") save(); if (e.key === "Escape") setEditing(false); }}
            onClick={e => e.stopPropagation()}
            onMouseDown={e => e.stopPropagation()}
            className="w-12 text-[11px] text-slate-900 bg-white rounded px-1 py-0.5 outline-none"
          />
        ) : (
          <button
            onClick={e => { e.stopPropagation(); setVal((duration / 60).toFixed(1)); setEditing(true); }}
            onMouseDown={e => e.stopPropagation()}
            className="text-[11px] text-slate-400 hover:text-white flex-shrink-0 tabular-nums"
            title="Edit group duration"
          >
            {(duration / 60).toFixed(1)}h
          </button>
        )
      }
    />
  );
}

function ShiftRow({ rootLocation }) {
  const dragData = makeDragData("shift", `shift-${rootLocation.id}`, `Shift – ${rootLocation.name}`, {
    root_location_id: rootLocation.id,
    duration_minutes: 480,
  });
  return (
    <DraggableListRow
      label={`Shift – ${rootLocation.name}`}
      color={SHIFT_COLOR}
      dragData={dragData}
      itemType="shift"
      extra={<span className="shrink-0 text-[11px] tabular-nums text-slate-400">8.0h</span>}
    />
  );
}

function DraggableUserRow({ user }) {
  const dragData = makeDragData("cleaner", user.user_id, user.name || user.email || "Unnamed",
    { shift_minutes: user.shift_minutes || 480 });
  return (
    <div draggable
      onDragStart={e => {
        e.dataTransfer.setData("application/iclean-plan", dragData);
        e.dataTransfer.setData("application/iclean-plantype-cleaner", "1");
        e.dataTransfer.effectAllowed = "copy";
      }}
      className="flex items-center gap-2 px-2 py-1.5 rounded border bg-slate-800 border-slate-600 text-slate-200 cursor-grab select-none"
    >
      <Avatar name={user.name || user.email} photo={user.photo_url} size="md" />
      <div className="min-w-0">
        <div className="text-[0.7rem] font-semibold truncate">{user.name || user.email || "Unnamed"}</div>
        {user.shift_minutes && (
          <div className="text-[11px] text-slate-400">{(user.shift_minutes / 60).toFixed(1)}h shift</div>
        )}
      </div>
    </div>
  );
}

// ── Drop cell (calendar cell drop target) ─────────────────────────────────────
function DropCell({ date, hour, allDay, onDrop, className, style, children }) {
  const [over, setOver] = React.useState(false);
  return (
    <div
      className={`${className || ""} ${over ? "bg-teal-50 ring-1 ring-teal-400 ring-inset" : ""}`}
      style={style}
      onDragOver={e => { e.preventDefault(); setOver(true); }}
      onDragLeave={() => setOver(false)}
      onDrop={e => {
        e.preventDefault(); setOver(false);
        const raw = e.dataTransfer.getData("application/iclean-plan")
              || e.dataTransfer.getData("application/iclean-move");
        if (!raw) return;
        try { onDrop(JSON.parse(raw), date, allDay ? null : hour); } catch {}
      }}
    >
      {children}
    </div>
  );
}

// ── Collapsible panel section ────────────────────────────────────────────────
function PanelSection({ title, children, defaultOpen = true }) {
  const [open, setOpen] = useState(defaultOpen);
  return (
    <section className="block w-full text-slate-100 border-b border-slate-800 p-4 text-xs">
      <button onClick={() => setOpen(p => !p)}
        className="w-full flex items-center justify-between mb-2">
        <span className="text-[0.7rem] text-slate-200 font-semibold uppercase tracking-wide">{title}</span>
        {open ? <ChevronUp size={12} className="text-slate-400" /> : <ChevronDown size={12} className="text-slate-400" />}
      </button>
      {open && <div className="space-y-1 border border-slate-700 rounded p-1">{children}</div>}
    </section>
  );
}

// ── Layout helper: group overlapping timed events into columns ────────────────
// Returns array of { event, col, totalCols }
function chipRank(e) {
  return (e.hasRoute && e.assigned_user_id) ? 0 : e.hasRoute ? 1 : 2;
}
function layoutTimedEvents(events, slotH) {
  // Sort by start time, then by type (combined→route→cleaner) within same start time
  const sorted = [...events].sort((a, b) => {
    const timeDiff = new Date(a.date) - new Date(b.date);
    return timeDiff !== 0 ? timeDiff : chipRank(a) - chipRank(b);
  });
  const columns = []; // each column is array of events (non-overlapping)
  const result = [];

  const hourOf = (d) => d.getHours() + d.getMinutes() / 60 + d.getSeconds() / 3600;
  sorted.forEach(ev => {
    const startH = hourOf(new Date(ev.date));

    let placed = false;
    for (let ci = 0; ci < columns.length; ci++) {
      const lastEv = columns[ci][columns[ci].length - 1];
      const lastEnd = hourOf(new Date(lastEv.date)) + (lastEv.estimated_minutes || 60) / 60;
      if (startH >= lastEnd - 1e-6) {
        columns[ci].push(ev);
        result.push({ event: ev, col: ci });
        placed = true;
        break;
      }
    }
    if (!placed) {
      columns.push([ev]);
      result.push({ event: ev, col: columns.length - 1 });
    }
  });

  const totalCols = columns.length || 1;
  return result.map(r => ({ ...r, totalCols }));
}

// ── Month view ───────────────────────────────────────────────────────────────
function MonthView({ currentDate, events, onDayClick, onDropPlan, onAssignCleaner, onAssignRoute, onAssignShift, onUnassignCleaner, onDelete }) {
  const year = currentDate.getFullYear();
  const month = currentDate.getMonth();
  const startOffset = (() => { const d = new Date(year, month, 1).getDay(); return d === 0 ? 6 : d - 1; })();
  const daysInMonth = new Date(year, month + 1, 0).getDate();
  const today = new Date();

  const cells = [];
  for (let i = 0; i < startOffset; i++) cells.push(null);
  for (let d = 1; d <= daysInMonth; d++) cells.push(new Date(year, month, d));
  while (cells.length % 7 !== 0) cells.push(null);
  const weeks = [];
  for (let i = 0; i < cells.length; i += 7) weeks.push(cells.slice(i, i + 7));

  return (
    <div className="flex flex-col h-full select-none">
      <div className="grid grid-cols-7 border-b border-slate-200">
        {WEEKDAYS_SHORT.map(d => (
          <div key={d} className="text-center text-[11px] font-semibold text-slate-400 py-1">{d}</div>
        ))}
      </div>
      <div className="flex-1 grid" style={{ gridTemplateRows: `repeat(${weeks.length}, minmax(0,1fr))` }}>
        {weeks.map((week, wi) => (
          <div key={wi} className="grid grid-cols-7">
            {week.map((day, di) => {
              if (!day) return <div key={di} className="border-r border-b border-slate-100 bg-slate-50" />;
              const isToday = isSameDay(day, today);
              const dayEvents = events.filter(e => e.date && isSameDay(new Date(e.date), day));
              return (
                <DropCell key={di} date={day} allDay onDrop={onDropPlan}
                  className="border-r border-b border-slate-100 p-1 hover:bg-slate-50 overflow-hidden">
                  <div className="flex items-center justify-between mb-0.5">
                    <div onClick={() => onDayClick?.(day)}
                      className={`text-[0.7rem] font-bold w-6 h-6 flex items-center justify-center rounded-full cursor-pointer
                        ${isToday ? "bg-teal-500 text-white" : "text-slate-600 hover:bg-slate-100"}`}>
                      {day.getDate()}
                    </div>
                    {dayEvents.length > 0 && <DayCopyButton day={day} />}
                  </div>
                  <div className="flex flex-row flex-nowrap overflow-hidden gap-1">
                    {sortChips(dayEvents).slice(0, 5).map((ev, ei) => (
                      <EventChip key={ei} event={ev} onAssignCleaner={onAssignCleaner} onAssignRoute={onAssignRoute} onAssignShift={onAssignShift} onUnassignCleaner={onUnassignCleaner} onDelete={onDelete}
                        className="flex-1 min-w-0" />
                    ))}
                    {dayEvents.length > 5 && (
                      <div className="text-[11px] text-slate-400 font-medium self-center px-0.5 flex-shrink-0">+{dayEvents.length - 5}</div>
                    )}
                  </div>
                </DropCell>
              );
            })}
          </div>
        ))}
      </div>
    </div>
  );
}

const DAY_COL_PX = 110; // fixed px per column in day view

// ── Tényadat elem: nem húzható, csak megjelenít ──────────────────────────────
function ActualChip({ event, style }) {
  const start = new Date(event.date);
  const end = new Date(start.getTime() + (event.estimated_minutes || 0) * 60000);
  const clock = (d) => `${String(d.getHours()).padStart(2, "0")}:${String(d.getMinutes()).padStart(2, "0")}`;
  const timeText = event.actualType === "cleaning" ? clock(start) : `${clock(start)}–${event.ongoing ? "now" : clock(end)}`;
  return (
    <div
      data-actual-type={event.actualType}
      className="group rounded-sm text-[11px] leading-tight text-slate-700"
      style={{
        ...style,
        backgroundColor: hexToRgba(event.color, 0.16),
        borderLeft: `3px solid ${event.color}`,
      }}
    >
      <div className="h-full overflow-hidden px-1">
        <div className="truncate font-semibold">{event.title}</div>
        <div className="truncate text-slate-500">{event.detail} · {timeText}</div>
      </div>
      <HoverTip>{event.detail}: {event.title} ({timeText})</HoverTip>
    </div>
  );
}

// Azonnal megjelenő buborék (a böngésző title-buboréka lassú).
function HoverTip({ children }) {
  return (
    <div className="pointer-events-none absolute bottom-full left-0 z-50 mb-1 hidden whitespace-nowrap rounded bg-slate-900 px-2 py-1 text-xs font-medium leading-5 text-white shadow-lg group-hover:block">
      {children}
    </div>
  );
}

// ── Timed events column for a single day ─────────────────────────────────────
// A mai nap oszlopában a jelenlegi időt mutató vonal; percenként frissül.
function NowLine({ day }) {
  const [now, setNow] = React.useState(() => new Date());
  React.useEffect(() => {
    const timer = setInterval(() => setNow(new Date()), 60000);
    return () => clearInterval(timer);
  }, []);
  if (!isSameDay(day, now)) return null;
  const top = (now.getHours() + now.getMinutes() / 60) * SLOT_H;
  return (
    <div data-testid="calendar-now-line" className="pointer-events-none absolute inset-x-0" style={{ top, zIndex: 12 }}>
      <div className="relative border-t-2 border-teal-600">
        <span className="absolute -left-1.5 -top-[5px] h-2 w-2 rounded-full bg-teal-600" />
      </div>
    </div>
  );
}

export function TimedEventsColumn({ day, events, onDropPlan, onAssignCleaner, onAssignRoute, onAssignShift, onPlaceInShift, onUnassignCleaner, onDelete, onResizeDone, hours, width = "100%", fixedColPx = null }) {
  const dayTimedEvs = events.filter(e =>
    e.date && isSameDay(new Date(e.date), day) && e.hasTime && !e.marker);
  const dayMarkers = events.filter(e => e.marker && e.date && isSameDay(new Date(e.date), day));
  // A shifthez kötött groupok a shift sávjában, a shiften belül jelennek meg.
  const dayShiftIds = new Set(dayTimedEvs.filter(e => e.is_shift).map(e => String(e.id).replace("plan-", "")));
  const inShift = dayTimedEvs.filter(e => !e.is_shift && e.shift_plan_id != null && dayShiftIds.has(String(e.shift_plan_id)));
  const laid = layoutTimedEvents(dayTimedEvs.filter(e => !inShift.includes(e)), SLOT_H);
  const maxCols = laid.reduce((m, r) => Math.max(m, r.totalCols), 1);
  const colWidth = fixedColPx ? fixedColPx * maxCols : null;
  const colRef = useRef(null);
  const [hoverHour, setHoverHour] = React.useState(null);
  const [shiftPreview, setShiftPreview] = React.useState(null);

  const chipBox = ({ col, totalCols }) => {
    const colFraction = totalCols === 1 ? 0.70 : 1 / totalCols;
    return fixedColPx
      ? { left: col * fixedColPx + 3, width: fixedColPx - 6 }
      : { left: `calc(${(col / totalCols) * 100}% + 3px)`, width: `calc(${colFraction * 100}% - 6px)`, fraction: colFraction, start: col / totalCols };
  };

  // Group húzása közben: a kurzor alatti shift, és hogy a group oda elfér-e.
  const shiftDropTarget = (e) => {
    if (!activeGroupDrag || !onPlaceInShift || !colRef.current) return null;
    const rect = colRef.current.getBoundingClientRect();
    const x = e.clientX - rect.left;
    const y = e.clientY - rect.top;
    const hit = laid.find(entry => {
      if (!entry.event.is_shift) return false;
      const box = chipBox(entry);
      const left = fixedColPx ? box.left : box.start * rect.width + 3;
      const w = fixedColPx ? box.width : box.fraction * rect.width - 6;
      const { start, end } = shiftWindow(entry.event);
      const dayStart = new Date(day.getFullYear(), day.getMonth(), day.getDate()).getTime();
      const top = ((start - dayStart) / 3600000) * SLOT_H;
      const bottom = ((end - dayStart) / 3600000) * SLOT_H;
      return x >= left && x <= left + w && y >= top && y <= bottom;
    });
    if (!hit) return null;
    const dayStart = new Date(day.getFullYear(), day.getMonth(), day.getDate()).getTime();
    const startMs = placeInShift(hit.event, dayStart + (y / SLOT_H) * 3600000, activeGroupDrag.minutes);
    return { entry: hit, startMs, dayStart };
  };

  const getHourFromY = (clientY) => {
    if (!colRef.current) return null;
    const rect = colRef.current.getBoundingClientRect();
    const y = clientY - rect.top;
    return Math.max(0, Math.min(23, Math.floor(y / SLOT_H)));
  };

  return (
    <div
      ref={colRef}
      className="relative border-l-2 border-slate-300"
      style={{ width: colWidth ? colWidth : width, minWidth: '100%' }}
      onDragOverCapture={e => {
        const target = shiftDropTarget(e);
        if (!target) { if (shiftPreview) setShiftPreview(null); return; }
        // A shift területén csak teljesen beleférő group ejthető; határon átlógó nem.
        e.stopPropagation();
        if (target.startMs == null) {
          e.dataTransfer.dropEffect = "none";
          setShiftPreview(null);
          return;
        }
        e.preventDefault();
        setHoverHour(null);
        setShiftPreview({
          ...chipBox(target.entry),
          top: ((target.startMs - target.dayStart) / 3600000) * SLOT_H,
          height: (activeGroupDrag.minutes / 60) * SLOT_H,
        });
      }}
      onDropCapture={e => {
        const target = shiftDropTarget(e);
        if (!target) return;
        e.preventDefault(); e.stopPropagation();
        setShiftPreview(null);
        const drag = activeGroupDrag;
        activeGroupDrag = null;
        if (target.startMs != null) onPlaceInShift(target.entry.event, drag, target.startMs);
      }}
      onDragOver={e => {
        const hasPlan = e.dataTransfer.types.includes("application/iclean-plan");
        const hasMove = e.dataTransfer.types.includes("application/iclean-move");
        if (hasPlan || hasMove) {
          e.preventDefault(); e.stopPropagation();
          setHoverHour(getHourFromY(e.clientY));
        }
      }}
      onDragLeave={e => {
        if (!colRef.current?.contains(e.relatedTarget)) { setHoverHour(null); setShiftPreview(null); }
      }}
      onDrop={e => {
        e.preventDefault(); e.stopPropagation();
        setHoverHour(null);
        const raw = e.dataTransfer.getData("application/iclean-plan")
              || e.dataTransfer.getData("application/iclean-move");
        if (!raw) return;
        const hour = getHourFromY(e.clientY);
        try { onDropPlan(JSON.parse(raw), day, hour); } catch {}
      }}
    >
      {hours.map(h => (
        <div key={h}
          className={`border-b border-slate-100 ${hoverHour === h ? "bg-teal-50 ring-1 ring-teal-400 ring-inset" : ""}`}
          style={{ height: SLOT_H }} />
      ))}
      <NowLine day={day} />
      {shiftPreview && (
        <div data-testid="shift-drop-preview" className="pointer-events-none absolute rounded-md border-2 border-dashed border-teal-500 bg-teal-400/20"
          style={{ zIndex: 13, top: shiftPreview.top, height: shiftPreview.height,
            left: fixedColPx ? shiftPreview.left + 6 : `calc(${shiftPreview.left.slice(5, -1)} + 6px)`,
            width: fixedColPx ? shiftPreview.width - 12 : `calc(${shiftPreview.width.slice(5, -1)} - 12px)` }} />
      )}
      {inShift.map(ev => {
        const host = laid.find(entry => entry.event.is_shift && String(entry.event.id).replace("plan-", "") === String(ev.shift_plan_id));
        const box = chipBox(host);
        const startDate = new Date(ev.date);
        const top = (startDate.getHours() + startDate.getMinutes() / 60) * SLOT_H;
        const durH = (ev.estimated_minutes || 60) / 60;
        return (
          <EventChip key={ev.id} event={ev} onAssignCleaner={onAssignCleaner} onAssignRoute={onAssignRoute} onUnassignCleaner={onUnassignCleaner} onDelete={onDelete} onResizeDone={onResizeDone}
            maxMinutes={maxMinutesInShift(host.event, ev)}
            style={{
              position: 'absolute', zIndex: 11,
              top: top + 2,
              height: Math.max(durH * SLOT_H - 4, 18),
              left: fixedColPx ? box.left + 6 : `calc(${box.left.slice(5, -1)} + 6px)`,
              width: fixedColPx ? box.width - 12 : `calc(${box.width.slice(5, -1)} - 12px)`,
            }} />
        );
      })}
      {dayMarkers.map((marker) => {
        // A takarítás jelölése annak az embernek a csíkján, akinél abban az időben
        // bejelentkezés futott; ha nincs ilyen, az oszlop jobb szélén.
        const t = marker.readTime ?? new Date(marker.date).getTime();
        const host = laid.find(({ event }) => event.actual && event.user_id != null
          && String(event.user_id) === String(marker.user_id)
          && t >= new Date(event.date).getTime()
          && t <= new Date(event.date).getTime() + (event.estimated_minutes || 0) * 60000);
        const d = new Date(marker.markerStart ?? t);
        const top = (d.getHours() + d.getMinutes() / 60 + d.getSeconds() / 3600) * SLOT_H;
        const markerHeight = Math.max(3, ((marker.markerMinutes || 0) / 60) * SLOT_H);
        const left = host ? `calc(${(host.col / host.totalCols) * 100}% + 3px)` : "calc(100% - 14px)";
        const width = host ? `calc(${(host.totalCols === 1 ? 0.7 : 1 / host.totalCols) * 100}% - 6px)` : "10px";
        const read = new Date(t);
        const clock = `${String(read.getHours()).padStart(2, "0")}:${String(read.getMinutes()).padStart(2, "0")}`;
        const duration = marker.markerMinutes ? ` · ${Math.max(1, Math.round(marker.markerMinutes * 60))} s` : "";
        return (
          <div
            key={marker.id}
            data-actual-type="cleaning"
            className="group"
            style={{ position: "absolute", zIndex: 11, top: marker.markerMinutes ? top : top - 1, height: markerHeight, left, width, backgroundColor: marker.color, borderRadius: 2 }}
          >
            <HoverTip>{marker.detail}: {marker.title} ({clock}{duration})</HoverTip>
          </div>
        );
      })}
      {laid.map(({ event: ev, col, totalCols }, idx) => {
        const startDate = new Date(ev.date);
        const startH = startDate.getHours() + startDate.getMinutes() / 60;
        const durH = (ev.estimated_minutes || 60) / 60;
        const top = startH * SLOT_H;
        const height = Math.max(durH * SLOT_H, 24);
        const chipW = fixedColPx ? fixedColPx - 6 : undefined;
        const chipLeft = fixedColPx ? col * fixedColPx + 3 : undefined;
        // Cap single-event chips at 70% so there's always room to drop another beside it
        const colFraction = totalCols === 1 ? 0.70 : 1 / totalCols;
        const pctW = `${colFraction * 100}%`;
        const pctLeft = `${(col / totalCols) * 100}%`;
        if (ev.actual) {
          return (
            <ActualChip key={ev.id} event={ev} style={{
              position: 'absolute', zIndex: 9,
              top: top + 1,
              height: Math.max(durH * SLOT_H - 2, 14),
              left: fixedColPx ? chipLeft : `calc(${pctLeft} + 3px)`,
              width: fixedColPx ? chipW : `calc(${pctW} - 6px)`,
            }} />
          );
        }
        return (
          <EventChip key={idx} event={ev} onAssignCleaner={onAssignCleaner} onAssignRoute={onAssignRoute} onAssignShift={onAssignShift} onUnassignCleaner={onUnassignCleaner} onDelete={onDelete} onResizeDone={onResizeDone}
            minMinutes={ev.is_shift ? minShiftMinutes(ev, events) : null}
            onDropThrough={(dropEvent) => {
              // Same-type drop on chip: create new plan at the chip's time position
              const raw = dropEvent.dataTransfer.getData("application/iclean-plan") || dropEvent.dataTransfer.getData("application/iclean-move");
              if (!raw) return;
              const hour = getHourFromY(dropEvent.clientY);
              try { onDropPlan(JSON.parse(raw), day, hour); } catch {}
            }}
            style={{
              position: 'absolute', zIndex: 10,
              top: top + 3,
              height: Math.max(height - 5, 22),
              left: fixedColPx ? chipLeft : `calc(${pctLeft} + 3px)`,
              width: fixedColPx ? chipW : `calc(${pctW} - 6px)`,
            }} />
        );
      })}
    </div>
  );
}

// ── Week view ────────────────────────────────────────────────────────────────
function WeekView({ currentDate, events, onDropPlan, onAssignCleaner, onAssignRoute, onAssignShift, onPlaceInShift, onUnassignCleaner, onDelete, onResizeDone }) {
  const monday = startOfWeek(currentDate);
  const days = Array.from({ length: 7 }, (_, i) => addDays(monday, i));
  const hours = Array.from({ length: 24 }, (_, i) => i);
  const today = new Date();
  const scrollRef = useRef(null);
  useEffect(() => { if (scrollRef.current) scrollRef.current.scrollTop = 6 * SLOT_H; }, []);

  return (
    <div className="flex flex-col h-full select-none overflow-hidden">
      {/* Day headers */}
      <div className="grid border-b border-slate-200 flex-shrink-0"
           style={{ gridTemplateColumns: "52px repeat(7, 1fr)" }}>
        <div />
        {days.map((d, i) => {
          const isToday = isSameDay(d, today);
          return (
            <div key={i} className="text-center py-1 border-l-2 border-slate-300">
              <div className="flex items-center justify-center gap-1 text-[11px] text-slate-400">
                {WEEKDAYS_SHORT[i]}
                {events.some(e => !e.actual && e.date && isSameDay(new Date(e.date), d)) && <DayCopyButton day={d} />}
              </div>
              <div className={`text-[0.8rem] font-bold mx-auto w-7 h-7 flex items-center justify-center rounded-full
                ${isToday ? "bg-teal-500 text-white" : "text-slate-600"}`}>
                {d.getDate()}
              </div>
            </div>
          );
        })}
      </div>
      {/* All-day row – auto height, chips side by side */}
      <div className="grid border-b border-slate-200 flex-shrink-0"
           style={{ gridTemplateColumns: "52px repeat(7, 1fr)" }}>
        <div className="text-[11px] text-slate-400 text-right pr-1 py-1 leading-tight">all<br/>day</div>
        {days.map((d, i) => {
          const allDayEvs = sortChips(events.filter(e => e.date && isSameDay(new Date(e.date), d) && !e.hasTime && !e.dateOnly));
          return (
            <DropCell key={i} date={d} allDay onDrop={onDropPlan}
              className="border-l-2 border-slate-300 min-h-[32px] p-0.5 flex flex-row flex-nowrap overflow-hidden gap-1 items-stretch">
              {allDayEvs.map((ev, ei) => (
                <EventChip key={ei} event={ev} onAssignCleaner={onAssignCleaner} onAssignRoute={onAssignRoute} onAssignShift={onAssignShift} onUnassignCleaner={onUnassignCleaner} onDelete={onDelete}
                  className="flex-1 min-w-0" />
              ))}
            </DropCell>
          );
        })}
      </div>
      {/* Date-only (unscheduled) row */}
      <div className="grid border-b border-slate-200 flex-shrink-0 bg-amber-50"
           style={{ gridTemplateColumns: "52px repeat(7, 1fr)" }}>
        <div className="text-[11px] text-amber-500 text-right pr-1 py-1 leading-tight font-semibold">date<br/>only</div>
        {days.map((d, i) => {
          const dateOnlyEvs = sortChips(events.filter(e => e.date && isSameDay(new Date(e.date), d) && e.dateOnly));
          return (
            <DropCell key={i} date={d} allDay onDrop={onDropPlan}
              className="border-l-2 border-slate-300 min-h-[32px] p-0.5 flex flex-row flex-nowrap overflow-hidden gap-1 items-stretch">
              {dateOnlyEvs.map((ev, ei) => (
                <EventChip key={ei} event={ev} onAssignCleaner={onAssignCleaner} onAssignRoute={onAssignRoute} onAssignShift={onAssignShift} onUnassignCleaner={onUnassignCleaner} onDelete={onDelete}
                  className="flex-1 min-w-0" />
              ))}
            </DropCell>
          );
        })}
      </div>
      {/* Time grid */}
      <div className="flex-1 overflow-y-auto" ref={scrollRef}>
        <div className="grid" style={{ gridTemplateColumns: "52px repeat(7, 1fr)" }}>
          {/* Time labels column */}
          <div>
            {hours.map(h => (
              <div key={h} className="text-[11px] text-slate-400 text-right pr-2 pt-1"
                   style={{ height: SLOT_H }}>
                {String(h).padStart(2, "0")}:00
              </div>
            ))}
          </div>
          {/* Day columns */}
          {days.map((d, di) => (
            <TimedEventsColumn key={di} day={d} events={events} hours={hours}
              onDropPlan={onDropPlan} onAssignCleaner={onAssignCleaner} onAssignRoute={onAssignRoute} onAssignShift={onAssignShift} onPlaceInShift={onPlaceInShift} onUnassignCleaner={onUnassignCleaner} onDelete={onDelete} onResizeDone={onResizeDone} />
          ))}
        </div>
      </div>
    </div>
  );
}

// ── Day view ─────────────────────────────────────────────────────────────────
// Napi nézet oszlopa: bal fele a terv (shift és a benne lévő egységek), jobb fele a tény
// (belépés, tracker, takarítások) a valós idején. Az egységek kerete átnyúlik a tény-sávba.
const LANE_PLAN_W = 150;
const LANE_FACT_W = 290;
const FACT_PILL_W = 116;
const FACT_STYLES = {
  login: { color: "#475569", prefix: "Login", detail: "Belépés a webappba" },
  logout: { color: "#94a3b8", prefix: "Logout", detail: "Kilépés a webappból" },
  tracker: { color: "#0f766e", prefix: "", detail: "Bejelentkezés a trackerre" },
  tracker_off: { color: "#5eaaa0", prefix: "Tracker off", detail: "Kijelentkezés a trackerről" },
};
const clockOfSeconds = (t) => new Date(t).toLocaleTimeString("hu-HU", { hour: "2-digit", minute: "2-digit", second: "2-digit" });

// Tény-elem a naptárban: ugyanaz a kártya-forma, mint a terveké (színes fejléc, keret).
// Egérrámutatásra ugyanez a kártya nagyban, minden adattal.
function ActualCard({ testId, color, title, top, left, width, height, detailTitle, details, children }) {
  return (
    <div data-testid={testId} className="group absolute z-[14] hover:z-[60]" style={{ top, left, width, height }}>
      <div className="relative h-full overflow-hidden rounded-md border-[1.5px]" style={{ borderColor: hexToRgba(color, 0.7), backgroundColor: hexToRgba(color, 0.12) }}>
        <div className="truncate px-1.5 text-[11px] font-bold leading-[17px] text-white" style={{ backgroundColor: hexToRgba(color, 0.85) }}>{title}</div>
        {children}
      </div>
      <div className="pointer-events-none absolute left-0 top-full z-50 mt-1 hidden w-80 overflow-hidden rounded-lg border-[1.5px] bg-white shadow-2xl group-hover:block" style={{ borderColor: hexToRgba(color, 0.7) }}>
        <div className="px-3 py-1.5 text-sm font-bold text-white" style={{ backgroundColor: hexToRgba(color, 0.9) }}>{detailTitle || title}</div>
        <div className="space-y-1 px-3 py-2 text-[13px] text-slate-700">
          {details.map((row, index) => (
            <div key={index} className="grid grid-cols-[4.6rem_minmax(0,1fr)] gap-2">
              <span className="tabular-nums text-slate-500">{row.label}</span>
              <span className="min-w-0">{row.value}</span>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}
const clockOf = (t) => {
  const d = new Date(t);
  return `${String(d.getHours()).padStart(2, "0")}:${String(d.getMinutes()).padStart(2, "0")}`;
};

function DayLane({ lane, day, allEvents, onDropPlan, onPlaceInShift, onAssignCleaner, onAssignRoute, onUnassignCleaner, onDelete, onResizeDone }) {
  const ref = useRef(null);
  const [hoverHour, setHoverHour] = React.useState(null);
  const [preview, setPreview] = React.useState(null);
  const dayStart = new Date(day.getFullYear(), day.getMonth(), day.getDate()).getTime();
  const yOf = (t) => ((t - dayStart) / 3600000) * SLOT_H;
  const hours = Array.from({ length: 24 }, (_, h) => h);
  const hourFromY = (clientY) => {
    const rect = ref.current.getBoundingClientRect();
    return Math.max(0, Math.min(23, Math.floor((clientY - rect.top) / SLOT_H)));
  };
  // Group húzása a shift terv-felületére: csak teljesen beleférő helyre ejthető.
  const shiftTarget = (e) => {
    if (!activeGroupDrag || !onPlaceInShift || !ref.current) return null;
    const rect = ref.current.getBoundingClientRect();
    const x = e.clientX - rect.left;
    const y = e.clientY - rect.top;
    if (x > LANE_PLAN_W) return null;
    const shift = lane.shifts.find((item) => {
      const { start, end } = shiftWindow(item);
      return y >= yOf(start) && y <= yOf(end);
    });
    if (!shift) return null;
    return { shift, startMs: placeInShift(shift, dayStart + (y / SLOT_H) * 3600000, activeGroupDrag.minutes) };
  };
  const chipProps = { onAssignCleaner, onAssignRoute, onUnassignCleaner, onDelete, onResizeDone };

  return (
    <div
      ref={ref}
      data-testid="day-lane"
      className="relative flex-shrink-0 border-l-2 border-slate-300"
      style={{ width: LANE_PLAN_W + LANE_FACT_W }}
      onDragOverCapture={(e) => {
        const target = shiftTarget(e);
        if (!target) { if (preview) setPreview(null); return; }
        e.stopPropagation();
        if (target.startMs == null) { e.dataTransfer.dropEffect = "none"; setPreview(null); return; }
        e.preventDefault();
        setHoverHour(null);
        setPreview({ top: yOf(target.startMs), height: (activeGroupDrag.minutes / 60) * SLOT_H });
      }}
      onDropCapture={(e) => {
        const target = shiftTarget(e);
        if (!target) return;
        e.preventDefault(); e.stopPropagation();
        setPreview(null);
        const drag = activeGroupDrag;
        activeGroupDrag = null;
        if (target.startMs != null) onPlaceInShift(target.shift, drag, target.startMs);
      }}
      onDragOver={(e) => {
        if (e.dataTransfer.types.includes("application/iclean-plan") || e.dataTransfer.types.includes("application/iclean-move")) {
          e.preventDefault(); e.stopPropagation();
          setHoverHour(hourFromY(e.clientY));
        }
      }}
      onDragLeave={(e) => { if (!ref.current?.contains(e.relatedTarget)) { setHoverHour(null); setPreview(null); } }}
      onDrop={(e) => {
        e.preventDefault(); e.stopPropagation();
        setHoverHour(null);
        const raw = e.dataTransfer.getData("application/iclean-plan") || e.dataTransfer.getData("application/iclean-move");
        if (!raw) return;
        try { onDropPlan(JSON.parse(raw), day, hourFromY(e.clientY)); } catch {}
      }}
    >
      {hours.map((h) => (
        <div key={h} className={`border-b border-slate-100 ${hoverHour === h ? "bg-teal-50" : ""}`} style={{ height: SLOT_H }} />
      ))}
      <div className="pointer-events-none absolute inset-y-0 border-l border-dashed border-slate-300" style={{ left: LANE_PLAN_W }} />
      <NowLine day={day} />

      {lane.shifts.map((shift) => {
        const { start, end } = shiftWindow(shift);
        return (
          <EventChip key={shift.id} event={shift} {...chipProps} inLane
            minMinutes={minShiftMinutes(shift, allEvents)}
            style={{ position: "absolute", zIndex: 5, left: 3, width: LANE_PLAN_W + LANE_FACT_W - 6, top: yOf(start) + 1, height: Math.max(yOf(end) - yOf(start) - 2, 22) }} />
        );
      })}

      {lane.units.map(({ event, shift, segments }) => {
        const start = new Date(event.date).getTime();
        const end = start + (event.estimated_minutes || 60) * 60000;
        const top = yOf(start) + 2;
        const height = Math.max(yOf(end) - yOf(start) - 4, 18);
        const color = event.color || "#0F766E";
        return (
          <React.Fragment key={event.id}>
            <div data-testid="unit-frame" className="pointer-events-none absolute"
              style={{ zIndex: 6, top, height, left: SHIFT_BAND_W + 8, width: LANE_PLAN_W + LANE_FACT_W - SHIFT_BAND_W - 16, border: `1px dashed ${hexToRgba(color, 0.55)}`, borderRadius: 6 }} />
            <EventChip event={event} {...chipProps} inLane
              maxMinutes={maxMinutesInShift(shift, event)}
              style={{ position: "absolute", zIndex: 11, top, height, left: SHIFT_BAND_W + 8, width: LANE_PLAN_W - SHIFT_BAND_W - 12 }} />
            {segments.map((segment) => (
              <div key={`${segment.kind}-${segment.start}`} data-testid={`unit-${segment.kind}`} className="pointer-events-none absolute"
                style={{
                  zIndex: 12, left: SHIFT_BAND_W + 8, width: LANE_PLAN_W - SHIFT_BAND_W - 12,
                  top: Math.max(yOf(segment.start), top), height: Math.max(Math.min(yOf(segment.end), top + height) - Math.max(yOf(segment.start), top), 1),
                  backgroundColor: segment.kind === "done" ? "rgba(20,184,166,0.22)" : "rgba(225,29,72,0.14)",
                  borderLeft: `3px solid ${segment.kind === "done" ? "#14b8a6" : "#e11d48"}`,
                  borderRadius: 3,
                }} />
            ))}
          </React.Fragment>
        );
      })}

      {lane.facts.reduce((placed, fact) => {
        // Közeli tények kártyái egymás alá csúsznak, hogy ne takarják egymást.
        const prev = placed[placed.length - 1];
        const top = Math.max(yOf(fact.time) - 10, prev ? prev.top + 22 : -Infinity);
        return [...placed, { ...fact, top }];
      }, []).map((fact, index) => {
        const style = FACT_STYLES[fact.kind];
        const title = style.prefix ? `${style.prefix} ${clockOf(fact.time)}` : `${fact.label} ${clockOf(fact.time)}`;
        return (
          <ActualCard key={`${fact.kind}-${fact.time}-${index}`} testId={`fact-${fact.kind}`} color={style.color} title={title}
            top={fact.top} left={LANE_PLAN_W + 6} width={FACT_PILL_W} height={20}
            detailTitle={style.detail}
            details={[
              { label: clockOfSeconds(fact.time), value: fact.kind === "login" || fact.kind === "logout" ? "Telefon (webapp)" : fact.label.replace(/ off$/, "") },
              ...(fact.user ? [{ label: "Dolgozó", value: fact.user }] : []),
            ]} />
        );
      })}

      {lane.cleaningGroups.reduce((placed, group) => {
        const prev = placed[placed.length - 1];
        const top = Math.max(yOf(group.start), prev ? prev.top + prev.height + 2 : -Infinity);
        const height = Math.max(yOf(group.end) - yOf(group.start), 20);
        return [...placed, { group, top, height }];
      }, []).map(({ group, top, height }, index) => (
        <ActualCard key={`clean-${group.locationId}-${group.start}-${index}`} testId="cleaning-label" color="#0d9488"
          title={group.items.length > 1 ? `${group.locationName} · ${group.items.length}×` : `${group.locationName} · ${group.items[0].technical_location_name || ""}`}
          top={top} left={LANE_PLAN_W + FACT_PILL_W + 12} width={LANE_FACT_W - FACT_PILL_W - 18} height={height}
          detailTitle={`${group.locationName} · ${group.items.length} takarítás`}
          details={group.items.map((item) => ({
            label: clockOfSeconds(item.read),
            value: `${item.technical_location_name || item.location_name}${item.duration_seconds ? ` · ${Math.round(item.duration_seconds)} s` : ""}${item.user_name ? ` · ${item.user_name}` : ""}`,
          }))}>
          {group.items.map((item) => (
            <div key={item.id} className="absolute right-1 w-1.5 rounded-sm bg-teal-600" style={{
              top: Math.max(0, ((item.start - group.start) / 3600000) * SLOT_H), height: Math.max(((item.end - item.start) / 3600000) * SLOT_H, 2),
            }} />
          ))}
        </ActualCard>
      ))}
    </div>
  );
}

export function DayView({ currentDate, events, plans = null, actual = null, groupsById = new Map(), onDropPlan, onAssignCleaner, onAssignRoute, onAssignShift, onPlaceInShift, onUnassignCleaner, onDelete, onResizeDone }) {
  const hours = Array.from({ length: 24 }, (_, i) => i);
  const planEvents = plans || events.filter(e => !e.actual);
  const dayEvents = planEvents.filter(e => e.date && isSameDay(new Date(e.date), currentDate));
  const allDayEvs = dayEvents.filter(e => !e.hasTime && !e.dateOnly);
  const dateOnlyEvs = dayEvents.filter(e => e.dateOnly);
  const { lanes, others } = buildDayLanes({ day: currentDate, plans: planEvents, actual, groupsById });
  const scrollRef = useRef(null);
  useEffect(() => {
    if (!scrollRef.current) return;
    const now = new Date();
    const hour = isSameDay(now, currentDate) ? Math.max(0, now.getHours() - 2) : 6;
    scrollRef.current.scrollTop = hour * SLOT_H;
  }, [currentDate]);
  const laneProps = { day: currentDate, allEvents: planEvents, onDropPlan, onPlaceInShift, onAssignCleaner, onAssignRoute, onUnassignCleaner, onDelete, onResizeDone };

  return (
    <div className="flex flex-col h-full overflow-hidden select-none">
      {/* Idő nélküli tételek: csak akkor van soruk, ha van ilyen */}
      {allDayEvs.length + dateOnlyEvs.length > 0 && (
        <DropCell date={currentDate} allDay onDrop={onDropPlan}
          className="border-b border-slate-200 min-h-[32px] px-1 py-0.5 flex flex-row flex-nowrap overflow-hidden gap-1 items-stretch flex-shrink-0">
          <span className="text-[11px] text-slate-400 self-center mr-1 flex-shrink-0 whitespace-nowrap">no time</span>
          {sortChips([...allDayEvs, ...dateOnlyEvs]).map((ev, ei) => (
            <EventChip key={ei} event={ev} onAssignCleaner={onAssignCleaner} onAssignRoute={onAssignRoute} onAssignShift={onAssignShift} onUnassignCleaner={onUnassignCleaner} onDelete={onDelete}
              className="flex-1 min-w-0" />
          ))}
        </DropCell>
      )}
      {/* Time grid: oszlopfejlécek és oszlopok egy közös görgetőben */}
      <div className="flex-1 overflow-auto" ref={scrollRef}>
        <div className="flex min-w-max">
          <div className="sticky left-0 z-30 flex-shrink-0 w-14 bg-white">
            <div className="sticky top-0 z-30 h-7 border-b border-slate-200 bg-white" />
            {hours.map(h => (
              <div key={h} className="text-[11px] text-slate-400 text-right pr-2 pt-1"
                   style={{ height: SLOT_H }}>
                {String(h).padStart(2, "0")}:00
              </div>
            ))}
          </div>
          {lanes.map((lane) => (
            <div key={lane.key} className="flex-shrink-0">
              <div className="sticky top-0 z-20 flex h-7 items-center border-b border-l-2 border-slate-200 border-l-slate-300 bg-white text-[11px] font-semibold text-slate-600"
                style={{ width: LANE_PLAN_W + LANE_FACT_W }}>
                <span className="truncate px-2" style={{ width: LANE_PLAN_W }}>{lane.shifts.length ? "Plan" : lane.title}</span>
                <span className="truncate px-2 text-slate-400" style={{ width: LANE_FACT_W }}>Actual</span>
              </div>
              <DayLane lane={lane} {...laneProps} />
            </div>
          ))}
          <div className="flex-shrink-0" style={{ minWidth: 2 * DAY_COL_PX }}>
            <div className="sticky top-0 z-20 flex h-7 items-center border-b border-l-2 border-slate-200 border-l-slate-300 bg-white px-2 text-[11px] font-semibold text-slate-500">
              Other plans
            </div>
            <TimedEventsColumn day={currentDate} events={others} hours={hours}
              onDropPlan={onDropPlan} onAssignCleaner={onAssignCleaner} onAssignRoute={onAssignRoute} onAssignShift={onAssignShift} onPlaceInShift={onPlaceInShift} onUnassignCleaner={onUnassignCleaner} onDelete={onDelete} onResizeDone={onResizeDone}
              fixedColPx={DAY_COL_PX} />
          </div>
        </div>
      </div>
    </div>
  );
}

// ── Main component ────────────────────────────────────────────────────────────
export default function IcleanViewCalendar({ calendarMode, selectedCompanyId, rootLocations = [], activeRootLocationId = null }) {
  const [view, setView] = useState("day");
  const [currentDate, setCurrentDate] = useState(new Date());
  // A naptárra váltva (pl. a Live View-ról) a mai nap nyílik meg napi nézetben.
  useEffect(() => {
    if (calendarMode) { setView("day"); setCurrentDate(new Date()); }
  }, [calendarMode]);
  const [routes, setRoutes] = useState([]);
  const [cleaners, setCleaners] = useState([]);
  const [cleaningGroups, setCleaningGroups] = useState([]);
  const [copyRequest, setCopyRequest] = useState(null); // { title, unit, planIds }
  const [actualData, setActualData] = useState(null);
  const [copyBusy, setCopyBusy] = useState(false);
  const [events, setEvents] = useState([]);
  const eventsRef = useRef([]);
  useEffect(() => { eventsRef.current = events; }, [events]);

  const loadData = useCallback(() => {
    if (!calendarMode || !selectedCompanyId) return;
    fetch("/api/clean-routes", { credentials: "include" })
      .then(r => r.json())
      .then(d => { if (d.success) setRoutes(d.routes || []); })
      .catch(() => {});
    fetch("/api/users", { credentials: "include" })
      .then(r => r.json())
      .then(d => { setCleaners((d.users || []).filter(u => u.type === "cleaner")); })
      .catch(() => {});
    fetch("/api/cleaning-groups", { credentials: "include" })
      .then(r => r.json())
      .then(d => { if (d.success) setCleaningGroups(d.groups || []); })
      .catch(() => {});
    fetch("/api/cleaning-records", { credentials: "include" })
      .then(r => r.json())
      .then(d => {
        if (!d.success) return;
        const mapped = (d.records || []).map(rec => ({
          id: rec.id,
          date: rec.actual_start || rec.planned_start,
          hasTime: !!(rec.actual_start || rec.planned_start),
          dateOnly: false,
          title: rec.route_name || rec.location_name || "Cleaning",
          route_code: rec.route_code || null,
          color: rec.status === "completed" ? "#10b981"
               : rec.status === "skipped"   ? "#64748b"
               : rec.status === "in_progress" ? "#f59e0b"
               : "#0F766E",
          status: rec.status,
          assigned_user_name: rec.user_name || null,
          assigned_user_photo: null,
          estimated_minutes: rec.estimated_minutes || null,
          root_location_id: rec.root_location_id || rec.location_id || null,
        }));
        // Only replace records, preserve plans (avoid race condition with plans fetch)
        setEvents(prev => [...prev.filter(e => String(e.id).startsWith("plan-")), ...mapped]);
      })
      .catch(() => {});
    fetch("/api/cleaning-plans", { credentials: "include" })
      .then(r => r.json())
      .then(d => {
        if (!d.success) return;
        const planned = (d.plans || []).filter(p => p.plan_date).map(plan => {
          const [py, pm, pd] = plan.plan_date.split("-").map(Number);
          let eventDate, hasTime, dateOnly;
          if (plan.scheduled_time) {
            const [h, m] = plan.scheduled_time.split(":").map(Number);
            eventDate = new Date(py, pm - 1, pd, h, m);
            hasTime = true;
            dateOnly = false;
          } else {
            eventDate = new Date(py, pm - 1, pd);
            hasTime = false;
            dateOnly = true; // date-specific but no start time
          }
          const label = plan.is_shift
            ? `Shift – ${plan.location_name || "Root location"}`
            : plan.route_name
            ? `${plan.route_code ? plan.route_code + " – " : ""}${plan.route_name}`
            : plan.cleaning_group_name || plan.assigned_user_name || plan.location_name || "Plan";
          return {
            id: `plan-${plan.id}`,
            date: eventDate.toISOString(),
            hasTime,
            dateOnly,
            title: label,
            route_code: plan.route_code || null,
            route_id: plan.route_id || null,
            cleaning_group_id: plan.cleaning_group_id || null,
            hasRoute: !!(plan.route_id || plan.cleaning_group_id),
            is_shift: !!plan.is_shift,
            color: plan.is_shift ? SHIFT_COLOR : plan.route_id ? "#0F766E" : plan.cleaning_group_id ? cleaningGroupColor(plan.cleaning_group_id) : plan.assigned_user_id ? SHIFT_COLOR : "#0e9488",
            status: "planned",
            assigned_user_id: plan.assigned_user_id || null,
            assigned_user_name: plan.assigned_user_name || null,
            assigned_user_photo: plan.assigned_user_photo || null,
            assigned_user_shift_minutes: plan.assigned_user_shift_minutes || 480,
            estimated_minutes: plan.estimated_minutes || null,
            root_location_id: plan.floorplan_location_id || plan.location_id || null,
            shift_plan_id: plan.shift_plan_id || null,
            shift_label: plan.shift_plan_id
              ? `Shift – ${plan.shift_location_name || "Root location"}`
              : null,
            shift_assigned_user_name: plan.shift_assigned_user_name || null,
          };
        });
        setEvents(prev => [
          ...prev.filter(e => !String(e.id).startsWith("plan-")),
          ...planned,
        ]);
      })
      .catch(() => {});
  }, [calendarMode, selectedCompanyId]);

  useEffect(() => { loadData(); }, [loadData]);

  // Tényadatok a látható hétre / napra; percenként frissül, így a még tartó elemek nőnek.
  useEffect(() => {
    if (!calendarMode || !selectedCompanyId || view === "month") { setActualData(null); return undefined; }
    const from = view === "week"
      ? startOfWeek(currentDate)
      : new Date(currentDate.getFullYear(), currentDate.getMonth(), currentDate.getDate());
    const to = addDays(from, view === "week" ? 7 : 1);
    let active = true;
    const load = () => {
      const params = new URLSearchParams({ from_ts: from.toISOString(), to_ts: to.toISOString() });
      fetch(`/api/calendar/actuals?${params}`, { credentials: "include" })
        .then(r => r.json())
        .then(d => { if (active && d.success) setActualData(d); })
        .catch(() => {});
    };
    load();
    const interval = setInterval(load, 60000);
    return () => { active = false; clearInterval(interval); };
  }, [calendarMode, selectedCompanyId, view, currentDate]);

  const navigate = useCallback((dir) => {
    setCurrentDate(prev => {
      const d = new Date(prev);
      if (view === "month") d.setMonth(d.getMonth() + dir);
      else if (view === "week") d.setDate(d.getDate() + dir * 7);
      else d.setDate(d.getDate() + dir);
      return d;
    });
  }, [view]);

  const titleLabel = () => {
    if (view === "month") return `${MONTHS[currentDate.getMonth()]} ${currentDate.getFullYear()}`;
    if (view === "week") {
      const mon = startOfWeek(currentDate);
      const sun = addDays(mon, 6);
      return `${MONTHS[mon.getMonth()].slice(0,3)} ${mon.getDate()} – ${MONTHS[sun.getMonth()].slice(0,3)} ${sun.getDate()}, ${sun.getFullYear()}`;
    }
    return `${MONTHS[currentDate.getMonth()]} ${currentDate.getDate()}, ${currentDate.getFullYear()}`;
  };

  const handleDropPlan = useCallback(async (dragData, date, hour) => {
    const isShift = dragData.type === "shift" || dragData.type === "cleaner";
    const shiftRootId = isShift
      ? (dragData.root_location_id || activeRootLocationId || (rootLocations.length === 1 ? rootLocations[0].id : null))
      : null;
    if (isShift && hour == null) {
      alert("Place the shift at a specific start time in Day or Week view.");
      return;
    }
    if (isShift && !shiftRootId) {
      alert("Select a root location before creating a shift.");
      return;
    }
    const shiftRoot = isShift
      ? rootLocations.find(location => Number(location.id) === Number(shiftRootId))
      : null;

    // Cleaner dragged off route chip onto a calendar cell
    if (dragData.__unassignFrom && dragData.type === "cleaner") {
      // If dropped back on the same day/hour as the source event, do nothing
      const srcEvent = eventsRef.current.find(ev => ev.id === dragData.__unassignFrom);
      if (srcEvent) {
        const exDate = new Date(srcEvent.date);
        const sameDay = isSameDay(exDate, date);
        const sameHour = hour == null ? !srcEvent.hasTime : (srcEvent.hasTime && exDate.getHours() === hour);
        if (sameDay && sameHour) return;
      }
      // 1. Break connection from original plan
      handleUnassignCleaner(dragData.__unassignFrom);
      // 2. Create a new standalone cleaner plan on the dropped cell
      // (fall through with the cleaner drag data - __unassignFrom is ignored below)
    } else if (dragData.__unassignFrom) {
      // generic unassign with no re-drop
      handleUnassignCleaner(dragData.__unassignFrom);
      return;
    }

    // Move existing calendar event
    if (dragData.__moveEventId) {
      const eventId = dragData.__moveEventId;
      // Self-drop check: if same day and same hour, do nothing
      const existing = eventsRef.current.find(ev => ev.id === eventId);
      if (existing) {
        const exDate = new Date(existing.date);
        const sameDay = isSameDay(exDate, date);
        const sameHour = hour == null ? !existing.hasTime : (existing.hasTime && exDate.getHours() === hour);
        if (sameDay && sameHour) return;
      }
      const y = date.getFullYear(), mo = date.getMonth(), d = date.getDate();
      const newDate = hour != null
        ? new Date(y, mo, d, hour, 0)
        : new Date(y, mo, d);
      const dateStr = localDateStr(date);
      const startTime = hour != null ? `${String(hour).padStart(2,"0")}:00` : null;
      // Áthelyezett shift viszi magával a groupjait; a shiftjéből kivitt group leválik róla.
      const movedShiftId = existing?.is_shift ? String(eventId).replace("plan-", "") : null;
      const delta = existing && hour != null && existing.hasTime ? newDate.getTime() - new Date(existing.date).getTime() : null;
      const carried = movedShiftId && delta != null
        ? eventsRef.current.filter(ev => !ev.is_shift && ev.shift_plan_id != null && String(ev.shift_plan_id) === movedShiftId)
        : [];
      const unlink = existing && !existing.is_shift && existing.shift_plan_id != null;
      setEvents(prev => prev.map(ev => {
        if (ev.id === eventId) {
          return { ...ev, date: newDate.toISOString(), hasTime: hour != null, dateOnly: hour == null,
            ...(unlink ? { shift_plan_id: null, shift_label: null, shift_assigned_user_name: null } : {}) };
        }
        if (carried.includes(ev)) return { ...ev, date: new Date(new Date(ev.date).getTime() + delta).toISOString() };
        return ev;
      }));
      const putPlan = (id, body) => {
        const planId = parseInt(String(id).replace("plan-", ""));
        if (isNaN(planId)) return;
        fetch(`/api/cleaning-plans/${planId}`, {
          method: "PUT", credentials: "include",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify(body),
        }).catch(() => {});
      };
      if (typeof eventId === "string" && eventId.startsWith("plan-")) {
        putPlan(eventId, { plan_date: dateStr, scheduled_time: startTime, ...(unlink ? { shift_plan_id: null } : {}) });
      }
      carried.forEach(ev => {
        const moved = new Date(new Date(ev.date).getTime() + delta);
        putPlan(ev.id, {
          plan_date: localDateStr(moved),
          scheduled_time: `${String(moved.getHours()).padStart(2, "0")}:${String(moved.getMinutes()).padStart(2, "0")}`,
        });
      });
      return;
    }

    const dateStr = localDateStr(date);
    const startTime = hour != null ? `${String(hour).padStart(2,"0")}:00` : null;

    // Find duration default
    let estimated_minutes = null;
    if (dragData.type === "route") {
      const r = routes.find(r => r.id === dragData.id);
      estimated_minutes = r?.duration_minutes || null;
    } else if (dragData.type === "cleaner") {
      estimated_minutes = dragData.shift_minutes || 480;
    } else if (dragData.type === "cleaning_group") {
      estimated_minutes = dragData.duration_minutes || 60;
    } else if (dragData.type === "shift") {
      estimated_minutes = dragData.duration_minutes || 480;
    }

    const payload = {
      plan_date: dateStr,
      scheduled_time: startTime,
      location_id: isShift ? shiftRootId : dragData.type === "location" ? dragData.id : dragData.type === "floorplan" ? dragData.id : null,
      floorplan_location_id: isShift ? shiftRootId : dragData.type === "floorplan" ? dragData.id : null,
      route_id: dragData.type === "route" ? dragData.id : null,
      assigned_user_id: dragData.type === "cleaner" ? dragData.id : null,
      cleaning_group_id: dragData.type === "cleaning_group" ? dragData.id : null,
      is_shift: isShift,
      estimated_minutes,
    };

    try {
      const res = await fetch("/api/cleaning-plans", {
        method: "POST", credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(payload),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok || !data.success) {
        alert(data.detail || "A terv nem jött létre.");
        return;
      }
      if (data.success) {
        const y = date.getFullYear(), mo = date.getMonth(), d = date.getDate();
        const eventDate = startTime ? new Date(y, mo, d, parseInt(startTime), 0) : new Date(y, mo, d);
        const hasTime = !!startTime;
        // Look up route code
        const route = dragData.type === "route" ? routes.find(r => r.id === dragData.id) : null;
        const assignedCleaner = dragData.type === "cleaner"
          ? cleaners.find(c => c.user_id === dragData.id)
          : null;
        setEvents(prev => [...prev, {
          id: `plan-${data.plan.id}`,
          date: eventDate.toISOString(),
          hasTime,
          dateOnly: !hasTime,
          title: isShift ? `Shift – ${shiftRoot?.name || "Root location"}` : dragData.label,
          route_code: route?.code || null,
          route_id: dragData.type === "route" ? dragData.id : null,
          cleaning_group_id: dragData.type === "cleaning_group" ? dragData.id : null,
          hasRoute: dragData.type === "route" || dragData.type === "cleaning_group",
          is_shift: isShift,
          color: isShift ? SHIFT_COLOR
               : dragData.type === "cleaner" ? SHIFT_COLOR
               : dragData.type === "route"   ? "#0F766E"
               : dragData.type === "cleaning_group" ? cleaningGroupColor(dragData.id)
               : "#0e9488",
          status: "planned",
          assigned_user_id: dragData.type === "cleaner" ? dragData.id : null,
          assigned_user_name: assignedCleaner?.name || assignedCleaner?.email || (dragData.type === "cleaner" ? dragData.label : null),
          assigned_user_photo: assignedCleaner?.photo_url || null,
          assigned_user_shift_minutes: assignedCleaner?.shift_minutes || dragData.shift_minutes || 480,
          estimated_minutes: data.plan.estimated_minutes || estimated_minutes,
          root_location_id: data.plan.floorplan_location_id || data.plan.location_id || payload.floorplan_location_id || payload.location_id || null,
        }]);
      }
    } catch (e) { console.error("Plan create error", e); }
  }, [routes, cleaners, activeRootLocationId, rootLocations]);

  const handleRouteDurationChange = useCallback(async (routeId, minutes) => {
    setRoutes(prev => prev.map(r => r.id === routeId ? { ...r, duration_minutes: minutes } : r));
    fetch(`/api/clean-routes/${routeId}`, {
      method: "PUT", credentials: "include",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ duration_minutes: minutes }),
    }).catch(() => {});
  }, []);

  const handleGroupDurationChange = useCallback(async (groupId, minutes) => {
    setCleaningGroups(prev => prev.map(g => g.id === groupId ? { ...g, duration_minutes: minutes } : g));
    try {
      const res = await fetch(`/api/cleaning-groups/${groupId}`, {
        method: "PATCH", credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ duration_minutes: minutes }),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok || !data.success) throw new Error(data.detail || "Saving the duration failed.");
      setCleaningGroups(prev => prev.map(g => g.id === groupId ? data.group : g));
    } catch (err) {
      alert(err.message);
      loadData();
    }
  }, [loadData]);

  const handleResizeDone = useCallback((eventId, newMinutes) => {
    setEvents(prev => prev.map(ev =>
      ev.id === eventId ? { ...ev, estimated_minutes: newMinutes } : ev
    ));
    if (typeof eventId === "string" && eventId.startsWith("plan-")) {
      const planId = parseInt(eventId.replace("plan-", ""));
      if (!isNaN(planId)) {
        fetch(`/api/cleaning-plans/${planId}`, {
          method: "PUT", credentials: "include",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ estimated_minutes: newMinutes, duration_manual: true }),
        }).catch(() => {});
      }
    }
  }, []);

  const handleDeletePlan = useCallback(async (eventId) => {
    setEvents(prev => prev.filter(ev => ev.id !== eventId));
    if (typeof eventId === "string" && eventId.startsWith("plan-")) {
      const planId = parseInt(eventId.replace("plan-", ""));
      if (!isNaN(planId)) {
        fetch(`/api/cleaning-plans/${planId}`, {
          method: "DELETE", credentials: "include",
        }).catch(() => {});
      }
    }
  }, []);

  const handleUnassignCleaner = useCallback(async (eventId) => {
    if (!String(eventId).startsWith("plan-")) return;
    const planId = parseInt(String(eventId).replace("plan-", ""));
    if (isNaN(planId)) return;
    setEvents(prev => prev.map(ev =>
      ev.id === eventId
        ? { ...ev, assigned_user_name: null, assigned_user_photo: null }
        : ev
    ));
    fetch(`/api/cleaning-plans/${planId}`, {
      method: "PUT", credentials: "include",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ assigned_user_id: null }),
    }).catch(() => {});
  }, []);

  const handleAssignRoute = useCallback(async (eventId, routeData) => {
    if (!String(eventId).startsWith("plan-")) return;
    const planId = parseInt(String(eventId).replace("plan-", ""));
    if (isNaN(planId)) return;
    const isGroup = routeData.type === "cleaning_group";
    const route = isGroup ? null : routes.find(r => r.id === routeData.id);
    setEvents(prev => prev.map(ev =>
      ev.id === eventId
        ? { ...ev,
            route_id: isGroup ? null : routeData.id,
            cleaning_group_id: isGroup ? routeData.id : null,
            route_code: route?.code || null, hasRoute: true,
            title: routeData.label, color: isGroup ? cleaningGroupColor(routeData.id) : "#0F766E",
            estimated_minutes: routeData.duration_minutes || ev.estimated_minutes }
        : ev
    ));
    fetch(`/api/cleaning-plans/${planId}`, {
      method: "PUT", credentials: "include",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({
        ...(isGroup ? { cleaning_group_id: routeData.id, route_id: null } : { route_id: routeData.id, cleaning_group_id: null }),
        ...(routeData.duration_minutes ? { estimated_minutes: routeData.duration_minutes } : {}) }),
    }).catch(() => {});
    // Ha naptár-chipből húzták (chip-to-chip merge), töröljük a forrás chip tervét
    if (routeData.__sourcePlanId && routeData.__sourcePlanId !== eventId) {
      const srcId = String(routeData.__sourcePlanId).replace("plan-", "");
      setEvents(prev => prev.filter(ev => ev.id !== routeData.__sourcePlanId));
      fetch(`/api/cleaning-plans/${srcId}`, { method: "DELETE", credentials: "include" }).catch(() => {});
    }
  }, [routes]);

  const handleAssignCleaner = useCallback(async (eventId, cleanerData) => {
    if (!String(eventId).startsWith("plan-")) return;
    const planId = parseInt(String(eventId).replace("plan-", ""));
    if (isNaN(planId)) return;
    const cleaner = cleaners.find(c => c.user_id === cleanerData.id);
    setEvents(prev => prev.map(ev =>
      ev.id === eventId
        ? {
            ...ev,
            assigned_user_id: cleanerData.id,
            assigned_user_name: cleaner?.name || cleaner?.email || cleanerData.label,
            assigned_user_photo: cleaner?.photo_url || null,
            assigned_user_shift_minutes: cleaner?.shift_minutes || 480,
          }
        : ev
    ));
    fetch(`/api/cleaning-plans/${planId}`, {
      method: "PUT", credentials: "include",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ assigned_user_id: cleanerData.id }),
    }).catch(() => {});
    // Ha egy shiftből húzták át a dolgozót, a forrás shift üresen megmarad.
    // A korábbi, önálló dolgozó-chip továbbra is megszűnik az áthelyezéskor.
    if (cleanerData.__sourcePlanId && cleanerData.__sourcePlanId !== eventId) {
      const srcId = String(cleanerData.__sourcePlanId).replace("plan-", "");
      if (cleanerData.__sourceIsShift) {
        setEvents(prev => prev.map(ev => ev.id === cleanerData.__sourcePlanId
          ? { ...ev, assigned_user_id: null, assigned_user_name: null, assigned_user_photo: null }
          : ev));
        fetch(`/api/cleaning-plans/${srcId}`, {
          method: "PUT", credentials: "include",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ assigned_user_id: null }),
        }).catch(() => {});
      } else {
        setEvents(prev => prev.filter(ev => ev.id !== cleanerData.__sourcePlanId));
        fetch(`/api/cleaning-plans/${srcId}`, { method: "DELETE", credentials: "include" }).catch(() => {});
      }
    }
  }, [cleaners]);

  const handleAssignShift = useCallback(async (shiftEventId, assignmentData) => {
    const sourceEventId = assignmentData?.__sourcePlanId;
    if (!String(sourceEventId).startsWith("plan-") || !String(shiftEventId).startsWith("plan-")) return;
    if (sourceEventId === shiftEventId) return;
    const sourcePlanId = parseInt(String(sourceEventId).replace("plan-", ""));
    const shiftPlanId = parseInt(String(shiftEventId).replace("plan-", ""));
    if (isNaN(sourcePlanId) || isNaN(shiftPlanId)) return;
    const shiftEvent = eventsRef.current.find(ev => ev.id === shiftEventId);
    if (!shiftEvent?.is_shift) return;

    setEvents(prev => prev.map(ev => ev.id === sourceEventId
      ? {
          ...ev,
          shift_plan_id: shiftPlanId,
          shift_label: shiftEvent.title || "Shift",
          shift_assigned_user_name: shiftEvent.assigned_user_name || null,
        }
      : ev));

    try {
      const response = await fetch(`/api/cleaning-plans/${sourcePlanId}`, {
        method: "PUT", credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ shift_plan_id: shiftPlanId }),
      });
      const data = await response.json().catch(() => ({}));
      if (!response.ok || !data.success) throw new Error(data.detail || "A shift hozzárendelése sikertelen.");
    } catch (error) {
      alert(error.message || "A shift hozzárendelése sikertelen.");
      loadData();
    }
  }, [loadData]);

  // Group ráhúzása egy shiftre: oda kerül, ahová ejtették, és a shifthez kötődik.
  // Meglévő groupnál áthelyezés + kötés, listából húzott groupnál új terv a shiftben.
  const handlePlaceInShift = async (shiftEvent, drag, startMs) => {
    if (!drag || !String(shiftEvent.id).startsWith("plan-")) return;
    const shiftPlanId = parseInt(String(shiftEvent.id).replace("plan-", ""));
    const start = new Date(startMs);
    const planDate = localDateStr(start);
    const scheduledTime = `${String(start.getHours()).padStart(2, "0")}:${String(start.getMinutes()).padStart(2, "0")}`;
    const shiftFields = { shift_plan_id: shiftPlanId, shift_label: shiftEvent.title || "Shift", shift_assigned_user_name: shiftEvent.assigned_user_name || null };
    try {
      if (drag.sourceEventId) {
        if (!String(drag.sourceEventId).startsWith("plan-")) return;
        setEvents(prev => prev.map(ev => ev.id === drag.sourceEventId
          ? { ...ev, date: start.toISOString(), hasTime: true, dateOnly: false, ...shiftFields }
          : ev));
        const res = await fetch(`/api/cleaning-plans/${String(drag.sourceEventId).replace("plan-", "")}`, {
          method: "PUT", credentials: "include",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ plan_date: planDate, scheduled_time: scheduledTime, shift_plan_id: shiftPlanId }),
        });
        const data = await res.json().catch(() => ({}));
        if (!res.ok || !data.success) throw new Error(data.detail || "A group shiftbe helyezése sikertelen.");
        return;
      }
      const res = await fetch("/api/cleaning-plans", {
        method: "POST", credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          plan_date: planDate, scheduled_time: scheduledTime, cleaning_group_id: drag.groupId,
          estimated_minutes: drag.minutes, shift_plan_id: shiftPlanId, is_shift: false,
        }),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok || !data.success) throw new Error(data.detail || "A group shiftbe helyezése sikertelen.");
      setEvents(prev => [...prev, {
        id: `plan-${data.plan.id}`,
        date: start.toISOString(), hasTime: true, dateOnly: false,
        title: drag.label, route_code: null, route_id: null,
        cleaning_group_id: drag.groupId, hasRoute: true, is_shift: false,
        color: cleaningGroupColor(drag.groupId), status: "planned",
        assigned_user_id: null, assigned_user_name: null, assigned_user_photo: null, assigned_user_shift_minutes: 480,
        estimated_minutes: data.plan.estimated_minutes || drag.minutes,
        root_location_id: data.plan.floorplan_location_id || data.plan.location_id || shiftEvent.root_location_id || null,
        ...shiftFields,
      }]);
    } catch (error) {
      alert(error.message);
      loadData();
    }
  };

  if (!calendarMode) return null;

  // A fejléc root-választója szűr, ugyanúgy, mint a térkép ráközelítése:
  // All (nincs aktív root) = minden, különben csak a kiválasztott benzinkút.
  const activeRootId = activeRootLocationId == null ? null : Number(activeRootLocationId);
  const groupById = new Map(cleaningGroups.map(group => [Number(group.id), group]));
  // A group-esemény benzinkútja mindig a group aktuális locationjeiből adódik.
  const eventRootId = event => {
    const group = event.cleaning_group_id ? groupById.get(Number(event.cleaning_group_id)) : null;
    return Number(group?.root_location_id || event.root_location_id);
  };
  const filteredEvents = events.filter(event => {
    const rootId = eventRootId(event);
    return activeRootId == null || !rootId || rootId === activeRootId;
  });
  // Benzinkút-hozzárendelés: kiválasztott rootnál csak a hozzá rendelt (vagy
  // hozzárendelés nélküli) emberek és trackerek látszanak.
  const assignedHere = (roots) => (
    activeRootId == null || !roots || roots.length === 0 || roots.map(Number).includes(activeRootId)
  );
  const userRootsById = new Map(cleaners.map(c => [String(c.user_id), c.root_location_ids || []]));
  const visibleCleaners = cleaners.filter(c => assignedHere(c.root_location_ids));
  const actualEvents = actualsToEvents(actualData).filter(event => (
    (activeRootId == null || !event.root_location_id || Number(event.root_location_id) === activeRootId)
    && (event.user_id == null || assignedHere(userRootsById.get(String(event.user_id))))
    && (event.device_root_location_id == null || assignedHere([event.device_root_location_id]))
  ));
  const timedViewEvents = [...filteredEvents, ...actualEvents];
  // A napi nézet a nyers tényadatokból dolgozik, ugyanazzal a root- és hozzárendelés-szűréssel.
  const userVisible = (userId) => userId == null || assignedHere(userRootsById.get(String(userId)));
  const visibleActual = actualData ? {
    web_sessions: (actualData.web_sessions || []).filter(s => userVisible(s.user_id)),
    tracker_sessions: (actualData.tracker_sessions || []).filter(s => userVisible(s.user_id)),
    cleanings: (actualData.cleanings || []).filter(c => userVisible(c.user_id)
      && (activeRootId == null || !c.root_location_id || Number(c.root_location_id) === activeRootId)),
  } : null;
  const visibleCleaningGroups = cleaningGroups.filter(group => (
    activeRootId == null || Number(group.root_location_id) === activeRootId
  ));
  const visibleRootLocations = rootLocations.filter(loc => (
    activeRootId == null || Number(loc.id) === activeRootId
  ));

  // Másolás: a kiválasztott benzinkút (vagy All) látható terveiből az adott időszakban.
  const plansBetween = (from, to) => filteredEvents
    .filter(ev => String(ev.id).startsWith("plan-") && ev.date)
    .filter(ev => { const d = new Date(ev.date); return d >= from && d < to; });

  const openCopy = (kind, target) => {
    let request;
    if (kind === "item") {
      request = {
        title: `Copy “${target.title}”`, unit: null,
        planIds: [Number(String(target.id).replace("plan-", ""))],
        shiftCount: target.is_shift ? 1 : 0,
        allowShiftSelection: false,
      };
    } else if (kind === "day") {
      const from = new Date(target.getFullYear(), target.getMonth(), target.getDate());
      const selected = plansBetween(from, addDays(from, 1));
      request = {
        title: `Copy ${localDateStr(from)}`, unit: "day",
        planIds: selected.map(event => Number(String(event.id).replace("plan-", ""))),
        shiftCount: selected.filter(event => event.is_shift).length,
        allowShiftSelection: true,
      };
    } else if (kind === "week") {
      const from = startOfWeek(currentDate);
      const selected = plansBetween(from, addDays(from, 7));
      request = {
        title: "Copy week", unit: "week",
        planIds: selected.map(event => Number(String(event.id).replace("plan-", ""))),
        shiftCount: selected.filter(event => event.is_shift).length,
        allowShiftSelection: true,
      };
    } else {
      const from = new Date(currentDate.getFullYear(), currentDate.getMonth(), 1);
      const selected = plansBetween(from, new Date(from.getFullYear(), from.getMonth() + 1, 1));
      request = {
        title: `Copy ${MONTHS[from.getMonth()]}`, unit: "month",
        planIds: selected.map(event => Number(String(event.id).replace("plan-", ""))),
        shiftCount: selected.filter(event => event.is_shift).length,
        allowShiftSelection: true,
      };
    }
    if (!request.planIds.length) {
      alert("There are no plans to copy here.");
      return;
    }
    setCopyRequest(request);
  };

  const confirmCopy = async (options) => {
    setCopyBusy(true);
    try {
      const res = await fetch("/api/cleaning-plans/copy", {
        method: "POST", credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ plan_ids: copyRequest.planIds, ...options }),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok || !data.success) throw new Error(data.detail || "Copy failed.");
      setCopyRequest(null);
      loadData();
      const skipped = [];
      if (data.skipped_past) skipped.push(`${data.skipped_past} in the past`);
      if (data.skipped_duplicate) skipped.push(`${data.skipped_duplicate} already planned`);
      alert(`${data.created} plan${data.created === 1 ? "" : "s"} created${skipped.length ? `; skipped: ${skipped.join(", ")}` : ""}.`);
    } catch (err) {
      alert(err.message);
    } finally {
      setCopyBusy(false);
    }
  };

  return (
    <CalendarCopyContext.Provider value={{ openCopy }}>
    <div className="flex w-full h-full overflow-hidden">
      {copyRequest && (
        <CalendarCopyDialog
          title={copyRequest.title}
          planCount={copyRequest.planIds.length}
          unit={copyRequest.unit}
          showShiftOption={copyRequest.allowShiftSelection && copyRequest.shiftCount > 0}
          busy={copyBusy}
          onConfirm={confirmCopy}
          onClose={() => setCopyRequest(null)}
        />
      )}
      {/* ── Calendar canvas ── */}
      <div className="flex-1 flex flex-col bg-white overflow-hidden">
        {/* Toolbar */}
        <div className="flex items-center gap-3 px-4 py-2 border-b border-slate-200 flex-shrink-0">
          <button onClick={() => setCurrentDate(new Date())}
            className="text-[0.7rem] px-2 py-1 rounded border border-slate-300 text-slate-600 hover:bg-slate-50 font-semibold">
            Today
          </button>
          <div className="flex items-center gap-1">
            <button onClick={() => navigate(-1)} className="p-1 rounded hover:bg-slate-100 text-slate-500">
              <ChevronLeft size={16} />
            </button>
            <span className="text-sm font-bold text-slate-700 min-w-[180px] text-center">{titleLabel()}</span>
            <button onClick={() => navigate(1)} className="p-1 rounded hover:bg-slate-100 text-slate-500">
              <ChevronRight size={16} />
            </button>
          </div>
          <button
            type="button"
            onClick={() => openCopy(view, currentDate)}
            className="ml-auto flex items-center gap-1 text-[0.7rem] px-2 py-1 rounded border border-slate-300 text-slate-600 hover:bg-slate-50 font-semibold"
            title={`Copy this ${view}`}
          >
            <Copy size={12} /> Copy {view}
          </button>
          <div className="flex rounded border border-slate-200 overflow-hidden text-[0.7rem]">
            {["month","week","day"].map(v => (
              <button key={v} onClick={() => setView(v)}
                className={`px-3 py-1 font-semibold ${view === v ? "bg-teal-500 text-white" : "bg-white text-slate-600 hover:bg-slate-50"}`}>
                {v === "month" ? "Month" : v === "week" ? "Week" : "Day"}
              </button>
            ))}
          </div>
        </div>
        {/* Calendar body */}
        <div className="flex-1 overflow-hidden">
          {view === "month" && <MonthView currentDate={currentDate} events={filteredEvents}
            onDayClick={d => { setCurrentDate(d); setView("day"); }}
            onDropPlan={handleDropPlan} onAssignCleaner={handleAssignCleaner} onAssignRoute={handleAssignRoute}
            onAssignShift={handleAssignShift}
            onUnassignCleaner={handleUnassignCleaner} onDelete={handleDeletePlan} />}
          {view === "week" && <WeekView currentDate={currentDate} events={timedViewEvents}
            onDropPlan={handleDropPlan} onAssignCleaner={handleAssignCleaner} onAssignRoute={handleAssignRoute}
            onAssignShift={handleAssignShift} onPlaceInShift={handlePlaceInShift}
            onUnassignCleaner={handleUnassignCleaner} onDelete={handleDeletePlan} onResizeDone={handleResizeDone} />}
          {view === "day" && <DayView currentDate={currentDate} events={timedViewEvents}
            plans={filteredEvents} actual={visibleActual} groupsById={groupById}
            onDropPlan={handleDropPlan} onAssignCleaner={handleAssignCleaner} onAssignRoute={handleAssignRoute}
            onAssignShift={handleAssignShift} onPlaceInShift={handlePlaceInShift}
            onUnassignCleaner={handleUnassignCleaner} onDelete={handleDeletePlan} onResizeDone={handleResizeDone} />}
        </div>
      </div>

      {/* ── Right panel ── */}
      <aside className="w-72 bg-slate-900 text-slate-100 flex flex-col border-l border-slate-700 overflow-y-auto flex-shrink-0">
        <div className="px-4 py-2 text-[11px] text-slate-500 border-b border-slate-800">
          Drag items onto the calendar to create plans
        </div>

        <PanelSection title="Floorplans">
          {visibleRootLocations.length === 0 && <div className="text-[11px] text-slate-400 px-1">No floorplans</div>}
          {visibleRootLocations.map((loc, i) => (
            <DraggableListRow key={loc.id} label={loc.name}
              color={ROUTE_PALETTE[i % ROUTE_PALETTE.length]}
              dragData={makeDragData("floorplan", loc.id, loc.name)} />
          ))}
        </PanelSection>

        <PanelSection title="Clean Routes">
          {routes.length === 0 && <div className="text-[11px] text-slate-400 px-1">No routes</div>}
          {routes.map((r, i) => (
            <RouteRow key={r.id} route={r} idx={i} onDurationChange={handleRouteDurationChange} />
          ))}
        </PanelSection>

        <PanelSection title="Cleaning Groups">
          {visibleCleaningGroups.length === 0 && <div className="text-[11px] text-slate-400 px-1">No cleaning groups</div>}
          {visibleCleaningGroups.map(group => (
            <GroupRow key={group.id} group={group} onDurationChange={handleGroupDurationChange} />
          ))}
        </PanelSection>

        <PanelSection title="Shifts">
          {visibleRootLocations.length === 0 && <div className="px-1 text-[11px] text-slate-400">No root locations</div>}
          {visibleRootLocations.map(location => (
            <ShiftRow key={location.id} rootLocation={location} />
          ))}
        </PanelSection>

        <PanelSection title="Cleaners">
          {visibleCleaners.length === 0 && <div className="text-[11px] text-slate-400 px-1">No cleaners</div>}
          {visibleCleaners.map(c => <DraggableUserRow key={c.user_id} user={c} />)}
        </PanelSection>
      </aside>
    </div>
    </CalendarCopyContext.Provider>
  );
}
